import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "@/_core/hooks/useAuth";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Building2, Users, TrendingUp, DollarSign, MoreVertical,
  CheckCircle2, XCircle, PauseCircle, Loader2, Search,
  Scissors, Crown, Shield, RefreshCw, MessageCircle, Sparkles, Phone,
  BarChart3, Activity, UserX, Mail, UserCheck, Eye,
  AlertCircle, RotateCcw, Save, ChevronDown, ChevronUp, Tv, Image, Bot, Instagram, Calendar, ShoppingCart, FileText, Mic, Percent, SendHorizontal,
  History, LogIn, Clock, BookOpen, Play, Trash2, Pencil, Plus, Video, Filter, MailCheck, MailX, QrCode, Wifi, Copy, Link2, EyeOff, Building, WalletCards, Package, UsersRound, Brain, Workflow, Plug, GraduationCap, LockKeyhole, Receipt, CreditCard, ClipboardCheck
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from "recharts";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { format, isValid } from "date-fns";
import { ptBR } from "date-fns/locale";
import { toast } from "sonner";
import { Link } from "wouter";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { buildSalonFeatureUpdate } from "@/lib/salonFeatureCatalog";
import { buildSalonHealthCards, filterSalonHealth } from "@/lib/salonHealthPresentation";
import { SalonHealthPanel } from "@/components/super-admin/SalonHealthPanel";
import { SalonBillingPanel } from "@/components/super-admin/SalonBillingPanel";
import { SalonOnboardingPanel } from "@/components/super-admin/SalonOnboardingPanel";
import { PlatformExecutiveReportsPanel } from "@/components/super-admin/PlatformExecutiveReportsPanel";
import { SalonIntegrationPanel } from "@/components/super-admin/SalonIntegrationPanel";
import { DetailedAuditPanel } from "@/components/super-admin/DetailedAuditPanel";
import { SuperAdminTabsNavigation } from "@/components/super-admin/SuperAdminTabsNavigation";
import { SuperAdminAccessGate } from "@/components/super-admin/SuperAdminAccessGate";
import { PlatformAdminAccessPanel } from "@/components/super-admin/PlatformAdminAccessPanel";
import { SalonSuccessCockpit } from "@/components/super-admin/SalonSuccessCockpit";
import { PlatformExecutionCenter } from "@/components/super-admin/PlatformExecutionCenter";
import { featureRolloutOrigin, filterFeatureRolloutSalons, type FeatureRolloutFilter } from "@/lib/featureRolloutPresentation";
import { toValidDate } from "@/lib/safeDate";
import { requestStaffAccessStatusChange, requestUserSessionLogout } from "@/lib/sensitiveAccessAction";
import { hasSuperAdminAccess } from "@shared/superAdminAccess";

// ─── Componente FeaturesTab ────────────────────────────────────────────────────
type SalonBasic = { id: number; name: string; slug: string; status: string; planId: number | null; createdAt: Date | null };

const FEATURE_LABELS: { key: string; label: string; description: string; icon: React.ReactNode; plan: string }[] = [
  { key: "hasOnlineBooking", label: "Agendamento Online", description: "Agenda pública para clientes", icon: <Calendar className="w-4 h-4" />, plan: "Todos" },
  { key: "hasCheckout", label: "Caixa / POS", description: "Módulo de pagamentos e checkout", icon: <ShoppingCart className="w-4 h-4" />, plan: "Todos" },
  { key: "hasAiChatbot", label: "Chatbot de IA", description: "Assistente virtual para clientes", icon: <Bot className="w-4 h-4" />, plan: "Professional+" },
  { key: "hasOnlinePayment", label: "Pagamento Online", description: "Stripe para pagamentos na agenda", icon: <DollarSign className="w-4 h-4" />, plan: "Professional+" },
  { key: "hasVoiceNotes", label: "Notas de Voz", description: "Gravação de notas de voz", icon: <Mic className="w-4 h-4" />, plan: "Premium+" },
  { key: "hasAdvancedReports", label: "Relatórios Avançados", description: "Relatórios detalhados de caixa e comissões", icon: <FileText className="w-4 h-4" />, plan: "Premium+" },
  { key: "hasCommissions", label: "Comissões", description: "Gestão de comissões por profissional", icon: <Percent className="w-4 h-4" />, plan: "Premium+" },
  { key: "hasPanelChamada", label: "Painel de Chamada (TV)", description: "Painel de fila para TV", icon: <Tv className="w-4 h-4" />, plan: "Elite" },
  { key: "hasMediaPromocional", label: "Mídia Promocional", description: "Slideshow de imagens/vídeos no painel TV", icon: <Image className="w-4 h-4" />, plan: "Elite" },
  { key: "hasAiWhatsapp", label: "IA no WhatsApp", description: "Agente de IA integrado ao WhatsApp", icon: <MessageCircle className="w-4 h-4" />, plan: "Elite" },
  { key: "hasAiInstagram", label: "IA no Instagram", description: "Agente de IA integrado ao Instagram", icon: <Instagram className="w-4 h-4" />, plan: "Elite" },
  { key: "hasBranches", label: "Filiais", description: "Gestão de unidades e comparativo entre filiais", icon: <Building className="w-4 h-4" />, plan: "Elite" },
  { key: "hasFinancial", label: "Financeiro", description: "Financeiro, pagamentos, comprovantes e fluxo de caixa", icon: <WalletCards className="w-4 h-4" />, plan: "Elite" },
  { key: "hasCrm", label: "CRM e Retenção", description: "CRM, fidelidade, pacotes, retornos e campanhas", icon: <UsersRound className="w-4 h-4" />, plan: "Elite" },
  { key: "hasInventory", label: "Estoque e Pedidos", description: "Produtos, estoque inteligente e pedidos", icon: <Package className="w-4 h-4" />, plan: "Elite" },
  { key: "hasOperations", label: "Operação e Equipe", description: "Profissionais, equipe, ponto e atendimento", icon: <Users className="w-4 h-4" />, plan: "Elite" },
  { key: "hasIntelligence", label: "Inteligência Executiva", description: "Metas, rentabilidade e indicadores operacionais", icon: <Brain className="w-4 h-4" />, plan: "Elite" },
  { key: "hasAutomation", label: "Omnichannel e Automações", description: "Central de IA, base de conhecimento e automações", icon: <Workflow className="w-4 h-4" />, plan: "Elite" },
  { key: "hasIntegrations", label: "Integrações", description: "Canais, Instagram e integrações externas", icon: <Plug className="w-4 h-4" />, plan: "Elite" },
  { key: "hasAcademy", label: "Academia e Tutorial", description: "Treinamentos, onboarding e trilhas da equipe", icon: <GraduationCap className="w-4 h-4" />, plan: "Elite" },
  { key: "hasSecurity", label: "Privacidade e Segurança", description: "Controles LGPD e segurança operacional", icon: <LockKeyhole className="w-4 h-4" />, plan: "Elite" },
  { key: "hasFiscal", label: "Fiscal", description: "Notas fiscais e controles fiscais do salão", icon: <Receipt className="w-4 h-4" />, plan: "Elite" },
];

export function FeaturesTab({ salons: _salons }: { salons: SalonBasic[] }) {
  const utils = trpc.useUtils();
  const { data: salonsWithFeatures, isLoading } = trpc.superAdmin.listSalonsWithFeatures.useQuery();
  const [expandedSalon, setExpandedSalon] = useState<number | null>(null);
  const [pendingChanges, setPendingChanges] = useState<Record<number, Record<string, boolean>>>({});
  const [savingId, setSavingId] = useState<number | null>(null);
  const [notesMap, setNotesMap] = useState<Record<number, string>>({});
  const [featSearch, setFeatSearch] = useState("");
  const [rolloutFilter, setRolloutFilter] = useState<FeatureRolloutFilter>("all");

  const updateFeatures = trpc.superAdmin.updateSalonFeatures.useMutation({
    onSuccess: (_, vars) => {
      toast.success("Funcionalidades atualizadas!");
      setSavingId(null);
      setPendingChanges(prev => { const n = { ...prev }; delete n[vars.salonId]; return n; });
      utils.superAdmin.listSalonsWithFeatures.invalidate();
    },
    onError: (e: any) => { toast.error(e.message); setSavingId(null); },
  });

  const resetToPlan = trpc.superAdmin.resetSalonFeaturesToPlan.useMutation({
    onSuccess: (data, vars) => {
      toast.success(`Funcionalidades redefinidas para o plano ${data.planSlug}!`);
      setPendingChanges(prev => { const n = { ...prev }; delete n[vars.salonId]; return n; });
      utils.superAdmin.listSalonsWithFeatures.invalidate();
    },
    onError: (e: any) => toast.error(e.message),
  });

  const handleToggle = (salonId: number, key: string, value: boolean) => {
    setPendingChanges(prev => ({
      ...prev,
      [salonId]: { ...(prev[salonId] ?? {}), [key]: value },
    }));
  };

  const handleSave = async (salonId: number, currentFeatures: Record<string, boolean | null>) => {
    const changes = pendingChanges[salonId] ?? {};
    const hasNoteUpdate = notesMap[salonId] !== undefined;
    if (Object.keys(changes).length === 0 && !hasNoteUpdate) return;
    setSavingId(salonId);
    await updateFeatures.mutateAsync(buildSalonFeatureUpdate(salonId, currentFeatures, changes, notesMap[salonId]) as any);
  };

  const filteredSalons = filterFeatureRolloutSalons(salonsWithFeatures ?? [], featSearch, rolloutFilter);
  const overriddenCount = (salonsWithFeatures ?? []).filter((salon) => salon.overriddenBySuperAdmin).length;

  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <Card key={i} className="animate-pulse"><CardContent className="p-6"><div className="h-12 bg-muted rounded" /></CardContent></Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-lg font-semibold">Gerenciamento de Funcionalidades</h2>
          <p className="text-sm text-muted-foreground">Gerencie exceções ao plano com rastreabilidade e restauração segura do conjunto contratado.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2"><div className="flex gap-1 rounded-lg border bg-muted/30 p-1">{(["all", "overridden", "plan"] as const).map((filter) => <Button key={filter} size="sm" variant={rolloutFilter === filter ? "default" : "ghost"} onClick={() => setRolloutFilter(filter)}>{filter === "all" ? "Todos" : filter === "overridden" ? `Overrides (${overriddenCount})` : "Somente plano"}</Button>)}</div><div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Buscar salão..."
            value={featSearch}
            onChange={e => setFeatSearch(e.target.value)}
            className="pl-9 w-64"
          />
        </div></div>
      </div>
      <div className="flex flex-wrap gap-2 text-xs text-muted-foreground"><Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-700">Conjunto do plano</Badge><span>recursos liberados pelo plano contratado</span><Badge variant="outline" className="ml-2 border-amber-200 bg-amber-50 text-amber-700">Liberação administrativa</Badge><span>exceção manual revisável e restaurável</span></div>

      {filteredSalons.length === 0 && (
        <Card><CardContent className="p-8 text-center text-muted-foreground">Nenhum salão encontrado.</CardContent></Card>
      )}

      {filteredSalons.map(salon => {
        const isExpanded = expandedSalon === salon.id;
        const pending = pendingChanges[salon.id] ?? {};
        const hasPending = Object.keys(pending).length > 0;
        const hasNoteChange = notesMap[salon.id] !== undefined && notesMap[salon.id] !== (salon.superAdminNotes ?? "");
        const currentFeatures: Record<string, boolean | null> = {
          hasOnlineBooking: salon.hasOnlineBooking,
          hasAiChatbot: salon.hasAiChatbot,
          hasOnlinePayment: salon.hasOnlinePayment,
          hasVoiceNotes: salon.hasVoiceNotes,
          hasAdvancedReports: salon.hasAdvancedReports,
          hasCommissions: salon.hasCommissions,
          hasCheckout: salon.hasCheckout,
          hasPanelChamada: salon.hasPanelChamada,
          hasMediaPromocional: salon.hasMediaPromocional,
          hasAiWhatsapp: salon.hasAiWhatsapp,
          hasAiInstagram: salon.hasAiInstagram,
          hasBranches: salon.hasBranches,
          hasFinancial: salon.hasFinancial,
          hasCrm: salon.hasCrm,
          hasInventory: salon.hasInventory,
          hasOperations: salon.hasOperations,
          hasIntelligence: salon.hasIntelligence,
          hasAutomation: salon.hasAutomation,
          hasIntegrations: salon.hasIntegrations,
          hasAcademy: salon.hasAcademy,
          hasSecurity: salon.hasSecurity,
          hasFiscal: salon.hasFiscal,
        };
        const enabledCount = Object.values({ ...currentFeatures, ...pending }).filter(Boolean).length;

        return (
          <Card key={salon.id} className={`transition-all ${salon.overriddenBySuperAdmin ? "border-amber-300 bg-amber-50/30" : ""}`}>
            <CardContent className="p-0">
              {/* Header do salão */}
              <button
                className="w-full flex items-center justify-between p-4 hover:bg-muted/30 transition-colors rounded-t-lg"
                onClick={() => setExpandedSalon(isExpanded ? null : salon.id)}
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Scissors className="w-4 h-4 text-primary" />
                  </div>
                  <div className="text-left">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm">{salon.name}</span>
                      {salon.overriddenBySuperAdmin && (
                        <Badge className="bg-amber-100 text-amber-700 border-amber-300 text-xs">Override ativo</Badge>
                      )}
                      {STATUS_BADGE[salon.status] ?? null}
                    </div>
                    <div className="text-xs text-muted-foreground">{enabledCount} de {FEATURE_LABELS.length} funcionalidades ativas</div>
                    <div className="mt-1 text-xs text-muted-foreground">Origem: <strong className={salon.overriddenBySuperAdmin ? "text-amber-700" : "text-emerald-700"}>{featureRolloutOrigin(salon.overriddenBySuperAdmin)}</strong></div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {hasPending && (
                    <Badge className="bg-blue-100 text-blue-700 border-blue-200 text-xs">{Object.keys(pending).length} alteração(ões) pendente(s)</Badge>
                  )}
                  {isExpanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                </div>
              </button>

              {/* Conteúdo expandido */}
              {isExpanded && (
                <div className="border-t border-border/50 p-4 space-y-4">
                  {/* Grid de features */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {FEATURE_LABELS.map(feat => {
                      const current = currentFeatures[feat.key];
                      const pendingVal = pending[feat.key];
                      const effective = pendingVal !== undefined ? pendingVal : (current ?? false);
                      const changed = pendingVal !== undefined && pendingVal !== current;

                      return (
                        <div
                          key={feat.key}
                          className={`flex items-center justify-between p-3 rounded-lg border transition-all ${
                            changed ? "border-blue-300 bg-blue-50/50" : effective ? "border-green-200 bg-green-50/30" : "border-border bg-muted/20"
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <span className={effective ? "text-green-600" : "text-muted-foreground"}>{feat.icon}</span>
                            <div>
                              <div className="text-xs font-medium">{feat.label}</div>
                              <div className="text-xs text-muted-foreground">{feat.plan} · {featureRolloutOrigin(salon.overriddenBySuperAdmin)}</div>
                            </div>
                          </div>
                          <Switch
                            checked={effective}
                            onCheckedChange={val => handleToggle(salon.id, feat.key, val)}
                          />
                        </div>
                      );
                    })}
                  </div>

                  {/* Notas do super admin */}
                  <div>
                    <Label className="text-xs text-muted-foreground mb-1 block">Notas internas (visível apenas para super admin)</Label>
                    <Textarea
                      placeholder="Observações sobre este salão..."
                      value={notesMap[salon.id] ?? salon.superAdminNotes ?? ""}
                      onChange={e => setNotesMap(prev => ({ ...prev, [salon.id]: e.target.value }))}
                      className="text-xs h-16 resize-none"
                    />
                  </div>

                  {/* Ações */}
                  <div className="flex items-center gap-2 pt-1">
                    <Button
                      size="sm"
                      onClick={() => handleSave(salon.id, currentFeatures)}
                      disabled={(!hasPending && !hasNoteChange) || savingId === salon.id}
                      className="gap-1.5"
                    >
                      {savingId === salon.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                      Salvar Alterações
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => { if (window.confirm(`Restaurar ${salon.name} para o conjunto do plano? As liberações administrativas serão removidas.`)) resetToPlan.mutate({ salonId: salon.id }); }}
                      disabled={resetToPlan.isPending}
                      className="gap-1.5"
                    >
                      <RotateCcw className="w-3 h-3" />
                      Redefinir para o Plano
                    </Button>
                    {hasPending && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setPendingChanges(prev => { const n = { ...prev }; delete n[salon.id]; return n; })}
                      >
                        Descartar
                      </Button>
                    )}
                    {salon.overriddenBySuperAdmin && (
                      <Badge className="ml-auto bg-amber-100 text-amber-700 border-amber-300 text-xs flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        Configuração manual ativa
                      </Badge>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

// Formata data com segurança — retorna fallback se o valor for inválido
function safeFormat(value: unknown, fmt: string, fallback = "—"): string {
  const date = toValidDate(value);
  return date && isValid(date) ? format(date, fmt, { locale: ptBR }) : fallback;
}

const STATUS_BADGE: Record<string, React.ReactNode> = {
  trial: <Badge className="bg-blue-100 text-blue-700 border-blue-200">Trial</Badge>,
  active: <Badge className="bg-green-100 text-green-700 border-green-200">Ativo</Badge>,
  suspended: <Badge className="bg-red-100 text-red-700 border-red-200">Suspenso</Badge>,
  cancelled: <Badge className="bg-gray-100 text-gray-600 border-gray-200">Cancelado</Badge>,
};

function getLeadWhatsAppUrl(phone?: string | null): string | null {
  const digits = (phone ?? "").replace(/\D/g, "");
  if (!digits) return null;
  const nationalNumber = digits.startsWith("55") ? digits : `55${digits}`;
  return `https://wa.me/${nationalNumber}`;
}

export default function SuperAdminPage() {
  const { user, isAuthenticated } = useAuth();
  const [search, setSearch] = useState("");
  const utils = trpc.useUtils();

  const { data: stats, isLoading: statsLoading } = trpc.superAdmin.getStats.useQuery();
  const [salonPage, setSalonPage] = useState(1);
  const [salonSearch, setSalonSearch] = useState("");
  const [salonStatus, setSalonStatus] = useState<"trial" | "active" | "suspended" | "cancelled" | undefined>(undefined);
  const { data: salonsData, isLoading: salonsLoading } = trpc.superAdmin.listSalons.useQuery({
    page: salonPage,
    pageSize: 20,
    search: salonSearch || undefined,
    status: salonStatus,
  });
  const { data: users, isLoading: usersLoading } = trpc.superAdmin.listUsers.useQuery();
  const { data: accessOverview, isLoading: accessOverviewLoading } = (trpc as any).superAdmin.accessOverview.useQuery();
  const { data: salonHealthOverview, isLoading: salonHealthLoading } = (trpc as any).superAdmin.salonHealthOverview.useQuery();
  const { data: billingOverview, isLoading: billingLoading } = (trpc as any).superAdmin.billingOverview.useQuery();
  const { data: salonOnboardingOverview, isLoading: onboardingLoading } = (trpc as any).superAdmin.salonOnboardingOverview.useQuery();
  const { data: integrationOverview, isLoading: integrationLoading } = (trpc as any).superAdmin.integrationOverview.useQuery();
  const { data: detailedAuditOverview, isLoading: detailedAuditLoading } = (trpc as any).superAdmin.detailedAuditOverview.useQuery();
  const createIntegrationFollowup = (trpc as any).superAdmin.createIntegrationFollowup.useMutation({
    onSuccess: () => {
      (utils as any).superAdmin.integrationOverview.invalidate();
      toast.success("Acompanhamento de integração registrado.");
    },
    onError: (error: any) => toast.error(error.message ?? "Não foi possível registrar o acompanhamento."),
  });
  const completeIntegrationFollowup = (trpc as any).superAdmin.completeIntegrationFollowup.useMutation({
    onSuccess: () => {
      (utils as any).superAdmin.integrationOverview.invalidate();
      toast.success("Acompanhamento concluído.");
    },
    onError: (error: any) => toast.error(error.message ?? "Não foi possível concluir o acompanhamento."),
  });
  const [reportPeriod, setReportPeriod] = useState<30 | 90 | 365>(30);
  const { data: platformExecutiveReport, isLoading: reportsLoading } = (trpc as any).superAdmin.platformExecutiveReport.useQuery({ periodDays: reportPeriod });
  const updateSalonOnboarding = (trpc as any).superAdmin.updateSalonOnboarding.useMutation({ onSuccess: () => utils.superAdmin.salonOnboardingOverview.invalidate() });
  const updateSalonBilling = (trpc as any).superAdmin.updateSalonBilling.useMutation({
    onSuccess: () => {
      (utils as any).superAdmin.billingOverview.invalidate();
      toast.success("Cobrança atualizada e registrada na auditoria.");
    },
    onError: (error: any) => toast.error(error.message ?? "Não foi possível atualizar a cobrança."),
  });
  const { data: channelRequests, isLoading: channelLoading } = trpc.superAdmin.listChannelRequests.useQuery();
  const { data: metrics, isLoading: metricsLoading } = trpc.superAdmin.getMetrics.useQuery();
  const { data: agentMetrics } = (trpc as any).superAdmin.getAiAgentMetrics.useQuery();

  const [activeTab, setActiveTab] = useState("salons");
  const [editingRequest, setEditingRequest] = useState<{ id: number; status: "pending" | "in_progress" | "done" | "cancelled"; adminNotes: string; adminContactPhone: string } | null>(null);
  const [clientSearch, setClientSearch] = useState("");
  const [clientPage, setClientPage] = useState(1);
  const [clientSalonFilter, setClientSalonFilter] = useState<number | null>(null);
  const [userSearch, setUserSearch] = useState("");
  const [accessSearch, setAccessSearch] = useState("");
  const [leadSearch, setLeadSearch] = useState("");

  const { data: allClients, isLoading: clientsLoading } = trpc.superAdmin.listAllClients.useQuery({
    page: clientPage,
    pageSize: 30,
    search: clientSearch || undefined,
    salonId: clientSalonFilter ?? undefined,
  });

  const { data: leads, isLoading: leadsLoading } = trpc.superAdmin.listLeads.useQuery();
  const filteredLeads = (leads as any[] ?? []).filter((lead: any) => {
    const query = leadSearch.trim().toLowerCase();
    if (!query) return true;
    return [lead.name, lead.email, lead.phone]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(query));
  });

  const forceLogout = trpc.superAdmin.forceLogoutUser.useMutation({
    onSuccess: (data: any) => {
      toast.success(data.message);
      utils.superAdmin.listUsers.invalidate();
      (utils.superAdmin as any).accessOverview.invalidate();
    },
    onError: (e: any) => toast.error(e.message),
  });

  const setStaffAccessStatus = (trpc as any).superAdmin.setStaffAccessStatus.useMutation({
    onSuccess: () => {
      toast.success("Status de acesso atualizado.");
      (utils.superAdmin as any).accessOverview.invalidate();
    },
    onError: (e: any) => toast.error(e.message),
  });

  // Modal de definir senha
  const [setPasswordModal, setSetPasswordModal] = useState<{ userId: number; userName: string } | null>(null);
  const [newPasswordInput, setNewPasswordInput] = useState("");
  const [showNewPasswordInput, setShowNewPasswordInput] = useState(false);
  const adminSetPassword = (trpc as any).superAdmin.adminSetPassword.useMutation({
    onSuccess: () => {
      toast.success("Senha definida com sucesso!");
      setSetPasswordModal(null);
      setNewPasswordInput("");
    },
    onError: (e: any) => toast.error(e.message),
  });

  const updateChannelRequest = trpc.superAdmin.updateChannelRequest.useMutation({
    onSuccess: () => {
      toast.success("Solicitação atualizada!");
      utils.superAdmin.listChannelRequests.invalidate();
      setEditingRequest(null);
    },
    onError: (e: any) => toast.error(e.message),
  });

  const pendingChannelCount = (channelRequests ?? []).filter(r => r.status === "pending").length;

  const activateSalon = trpc.superAdmin.activateSalon.useMutation({
    onSuccess: () => { toast.success("Salão ativado!"); utils.superAdmin.listSalons.invalidate(); utils.superAdmin.getStats.invalidate(); },
    onError: (e: any) => toast.error(e.message),
  });

  const suspendSalon = trpc.superAdmin.suspendSalon.useMutation({
    onSuccess: () => { toast.success("Salão suspenso."); utils.superAdmin.listSalons.invalidate(); utils.superAdmin.getStats.invalidate(); },
    onError: (e: any) => toast.error(e.message),
  });

  const cancelSalon = trpc.superAdmin.cancelSalon.useMutation({
    onSuccess: () => { toast.success("Salão cancelado."); utils.superAdmin.listSalons.invalidate(); utils.superAdmin.getStats.invalidate(); },
    onError: (e: any) => toast.error(e.message),
  });
  const sendMonthlyReport = trpc.superAdmin.sendMonthlyReportToSalon.useMutation({
    onSuccess: (data: any) => toast.success(`Relatório de ${data.month}/${data.year} enviado para ${data.sentTo}!`),
    onError: (e: any) => toast.error(e.message),
  });
  // ─── Criar Salão ─────────────────────────────────────────────────────────────
  const [showCreateSalon, setShowCreateSalon] = useState(false);
  const [newSalonForm, setNewSalonForm] = useState({
    ownerName: "", ownerEmail: "", name: "", slug: "", description: "",
    address: "", phone: "", email: "", planId: "none",
    status: "trial" as "trial" | "active" | "suspended" | "cancelled",
    trialDays: 15,
  });
  const { data: plansList } = trpc.plans.list.useQuery();
  // ─── Filtro por plano na listagem ─────────────────────────────────────────────
  const [salonPlanFilter, setSalonPlanFilter] = useState<string>("all");
  // ─── Validação de slug em tempo real ─────────────────────────────────────────
  const [slugToCheck, setSlugToCheck] = useState("");
  const slugDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { data: slugCheck, isFetching: slugChecking } = (trpc as any).superAdmin.checkSlugAvailability.useQuery(
    { slug: slugToCheck },
    { enabled: slugToCheck.length >= 2 }
  );
  const handleSlugChange = (raw: string) => {
    const clean = raw.toLowerCase().replace(/[^a-z0-9-]/g, '');
    setNewSalonForm(f => ({ ...f, slug: clean }));
    if (slugDebounceRef.current) clearTimeout(slugDebounceRef.current);
    slugDebounceRef.current = setTimeout(() => setSlugToCheck(clean), 500);
  };
  // Quando o nome muda, atualiza o slug e agenda verificação
  const handleNameChange = (name: string) => {
    const autoSlug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    setNewSalonForm(f => ({ ...f, name, slug: autoSlug }));
    if (slugDebounceRef.current) clearTimeout(slugDebounceRef.current);
    slugDebounceRef.current = setTimeout(() => setSlugToCheck(autoSlug), 500);
  };
  // ─── Reenviar e-mail de boas-vindas ──────────────────────────────────────────
  const resendWelcomeEmail = (trpc as any).superAdmin.resendWelcomeEmail.useMutation({
    onSuccess: (data: any) => toast.success(`E-mail reenviado para ${data.email}!`),
    onError: (e: any) => toast.error(e.message),
  });
  const createSalonMutation = (trpc as any).superAdmin.createSalonBySuperAdmin.useMutation({
    onSuccess: (data: any) => {
      if (data.emailSent) {
        toast.success(`Salão "${newSalonForm.name}" criado! E-mail enviado para ${data.ownerEmail}.`);
      } else {
        toast.warning(`Salão "${newSalonForm.name}" criado, mas o e-mail de boas-vindas não pôde ser enviado.`);
      }
      utils.superAdmin.listSalons.invalidate();
      utils.superAdmin.getStats.invalidate();
      setShowCreateSalon(false);
      setSlugToCheck("");
      setNewSalonForm({ ownerName: "", ownerEmail: "", name: "", slug: "", description: "", address: "", phone: "", email: "", planId: "none", status: "trial", trialDays: 15 });
    },
    onError: (e: any) => toast.error(e.message),
  });
  // ─── Editar Salão ─────────────────────────────────────────────────────────────
  const [editingSalon, setEditingSalon] = useState<any | null>(null);
  const [editSalonForm, setEditSalonForm] = useState({ name: "", slug: "", description: "", address: "", phone: "", email: "", planId: "", status: "trial" as "trial" | "active" | "suspended" | "cancelled", contractImplementationFee: "0", contractMonthlyFee: "0", queuePanelProfile: "tanning" });
  const [evolutionForm, setEvolutionForm] = useState({ evolutionApiUrl: "", evolutionApiKey: "", instanceName: "" });
  const [showEvolutionApiKey, setShowEvolutionApiKey] = useState(false);
  const [evolutionQr, setEvolutionQr] = useState<string | null>(null);
  const [evolutionQrText, setEvolutionQrText] = useState<string | null>(null);
  const [evolutionStatus, setEvolutionStatus] = useState<{ connected: boolean; state: string } | null>(null);
  const [configuredWebhookUrl, setConfiguredWebhookUrl] = useState<string | null>(null);
  const updateSalonMutation = (trpc as any).superAdmin.updateSalonBySuperAdmin.useMutation({
    onSuccess: () => {
      toast.success("Salão atualizado com sucesso!");
      utils.superAdmin.listSalons.invalidate();
      setEditingSalon(null);
    },
    onError: (e: any) => toast.error(e.message),
  });
  const updateEvolutionConfig = (trpc as any).superAdmin.updateEvolutionConfig.useMutation({
    onSuccess: () => {
      toast.success("Configuração da Evolution API salva!");
      utils.superAdmin.listSalons.invalidate();
      setEditingSalon((salon: any) => salon ? { ...salon, evolutionApiUrl: evolutionForm.evolutionApiUrl, evolutionInstance: evolutionForm.instanceName } : salon);
    },
    onError: (e: any) => toast.error(e.message),
  });
  const testEvolutionConfig = (trpc as any).superAdmin.testEvolutionConfig.useMutation({
    onSuccess: (data: any) => {
      setEvolutionStatus(data);
      if (data.connected) toast.success("WhatsApp conectado com sucesso!");
      else if (data.state === "not_configured") toast.info("Preencha a URL, API Key e o nome da instância primeiro.");
      else toast.info(`Status da instância: ${data.state ?? "desconhecido"}.`);
    },
    onError: (e: any) => toast.error(e.message),
  });
  const createEvolutionInstance = (trpc as any).superAdmin.createEvolutionInstance.useMutation({
    onSuccess: (data: any) => {
      setEvolutionStatus({ connected: data.connected === true || data.state === "open", state: data.state ?? "connecting" });
      setEvolutionQr(data.qrcode ?? null);
      setEvolutionQrText(data.qrText ?? data.pairingCode ?? null);
      utils.superAdmin.listSalons.invalidate();
      setEditingSalon((salon: any) => salon ? { ...salon, evolutionApiUrl: evolutionForm.evolutionApiUrl, evolutionInstance: evolutionForm.instanceName } : salon);
      toast.success(data.connected ? "WhatsApp já está conectado." : data.alreadyExisted ? "QR Code atualizado para a instância existente." : "Instância criada. Escaneie o QR Code.");
    },
    onError: (e: any) => toast.error(e.message),
  });
  const setEvolutionWebhook = (trpc as any).superAdmin.setEvolutionWebhook.useMutation({
    onSuccess: (data: any) => {
      setConfiguredWebhookUrl(data.webhookUrl);
      toast.success("Webhook configurado para esta instância.");
    },
    onError: (e: any) => toast.error(e.message),
  });
  const handleEditSalon = (salon: any) => {
    setEditingSalon(salon);
    setEvolutionForm({ evolutionApiUrl: salon.evolutionApiUrl ?? "", evolutionApiKey: "", instanceName: salon.evolutionInstance ?? `salon-${salon.id}` });
    setShowEvolutionApiKey(false);
    setEvolutionQr(null);
    setEvolutionQrText(null);
    setEvolutionStatus(null);
    setConfiguredWebhookUrl(null);
    setEditSalonForm({
      name: salon.name ?? "", slug: salon.slug ?? "", description: salon.description ?? "",
      address: salon.address ?? "", phone: salon.phone ?? "", email: salon.email ?? "",
      planId: salon.planId ? String(salon.planId) : "none",
      status: salon.status ?? "trial",
      contractImplementationFee: String(Number(salon.contractImplementationFee ?? 0)),
      contractMonthlyFee: String(Number(salon.contractMonthlyFee ?? 0)),
      queuePanelProfile: salon.queuePanelProfile ?? "tanning",
    });
  };
  const handleUpdateSalon = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingSalon) return;
    updateSalonMutation.mutate({
      salonId: editingSalon.id,
      name: editSalonForm.name || undefined,
      slug: editSalonForm.slug || undefined,
      description: editSalonForm.description || undefined,
      address: editSalonForm.address || undefined,
      phone: editSalonForm.phone || undefined,
      email: editSalonForm.email || undefined,
      planId: editSalonForm.planId && editSalonForm.planId !== "none" ? Number(editSalonForm.planId) : undefined,
      status: editSalonForm.status,
      contractImplementationFee: Number(editSalonForm.contractImplementationFee || 0),
      contractMonthlyFee: Number(editSalonForm.contractMonthlyFee || 0),
      queuePanelProfile: editSalonForm.queuePanelProfile,
    });
  };
  const saveEvolutionConfiguration = () => {
    if (!editingSalon) return;
    updateEvolutionConfig.mutate({
      salonId: editingSalon.id,
      evolutionApiUrl: evolutionForm.evolutionApiUrl.trim() || undefined,
      evolutionInstance: evolutionForm.instanceName.trim() || undefined,
      ...(evolutionForm.evolutionApiKey.trim() ? { evolutionApiKey: evolutionForm.evolutionApiKey.trim() } : {}),
    });
  };
  const createOrConnectEvolutionInstance = () => {
    if (!editingSalon) return;
    createEvolutionInstance.mutate({
      salonId: editingSalon.id,
      evolutionApiUrl: evolutionForm.evolutionApiUrl.trim() || undefined,
      evolutionApiKey: evolutionForm.evolutionApiKey.trim() || undefined,
      instanceName: evolutionForm.instanceName.trim() || undefined,
      connectMode: "qr",
    });
  };
  const handleCreateSalon = (e: React.FormEvent) => {
    e.preventDefault();
    createSalonMutation.mutate({
      ownerName: newSalonForm.ownerName, ownerEmail: newSalonForm.ownerEmail,
      name: newSalonForm.name, slug: newSalonForm.slug,
      description: newSalonForm.description || undefined,
      address: newSalonForm.address || undefined, phone: newSalonForm.phone || undefined,
      email: newSalonForm.email || undefined,
      planId: newSalonForm.planId && newSalonForm.planId !== "none" ? Number(newSalonForm.planId) : undefined,
      status: newSalonForm.status, trialDays: newSalonForm.trialDays,
    });
  };
  const isCreateSalonBlocked = createSalonMutation.isPending || (slugToCheck.length >= 2 && !slugChecking && slugCheck?.available === false);

  if (!isAuthenticated || !hasSuperAdminAccess(user)) {
    return <SuperAdminAccessGate isAuthenticated={isAuthenticated} user={user}>Conteúdo administrativo</SuperAdminAccessGate>;
  }

  const filteredSalons = (salonsData?.data ?? []).filter((salon: any) => {
    if (salonPlanFilter === "all") return true;
    if (salonPlanFilter === "none") return !salon.planId;
    return String(salon.planId) === salonPlanFilter;
  });

  const totalRevenue = stats?.revenue?.total ? Number(stats.revenue.total) : 0;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <nav className="border-b border-border/50 bg-white/90 backdrop-blur-md sticky top-0 z-50">
        <div className="container flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
              <Crown className="w-4 h-4 text-primary-foreground" />
            </div>
            <div>
              <span className="font-semibold text-foreground" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.1rem" }}>
                Super Admin
              </span>
              <Badge variant="secondary" className="ml-2 text-xs">Plataforma Beleza & Bronze</Badge>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground">{user?.name}</span>
            <Link href="/superadmin/agentes">
              <Button variant="outline" size="sm" className="gap-1.5">
                <Bot className="h-3.5 w-3.5" />
                Agentes IA
              </Button>
            </Link>
            <Link href="/">
              <Button variant="outline" size="sm">Ver Site</Button>
            </Link>
          </div>
        </div>
      </nav>

      <div className="container py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6 md:mb-8">
          {statsLoading ? (
            Array.from({ length: 4 }).map((_, i) => (
              <Card key={i} className="animate-pulse">
                <CardContent className="p-6">
                  <div className="h-4 bg-muted rounded w-3/4 mb-3" />
                  <div className="h-8 bg-muted rounded w-1/2" />
                </CardContent>
              </Card>
            ))
          ) : (
            <>
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <Building2 className="w-5 h-5 text-primary" />
                    <span className="text-sm text-muted-foreground">Total de Salões</span>
                  </div>
                  <div className="text-3xl font-light text-foreground">{stats?.salonStats?.total ?? 0}</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {stats?.salonStats?.active ?? 0} ativos · {stats?.salonStats?.trial ?? 0} em trial
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <CheckCircle2 className="w-5 h-5 text-green-500" />
                    <span className="text-sm text-muted-foreground">Ativos</span>
                  </div>
                  <div className="text-3xl font-light text-foreground">{stats?.salonStats?.active ?? 0}</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {stats?.salonStats?.suspended ?? 0} suspensos
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <Users className="w-5 h-5 text-blue-500" />
                    <span className="text-sm text-muted-foreground">Usuários</span>
                  </div>
                  <div className="text-3xl font-light text-foreground">{stats?.recentUsers?.length ?? 0}</div>
                  <div className="text-xs text-muted-foreground mt-1">cadastrados</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <DollarSign className="w-5 h-5 text-amber-500" />
                    <span className="text-sm text-muted-foreground">Receita (mês)</span>
                  </div>
                  <div className="text-3xl font-light text-foreground">
                    R$ {totalRevenue.toFixed(0)}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">agendamentos concluídos</div>
                </CardContent>
              </Card>
            </>
          )}
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <SuperAdminTabsNavigation activeTab={activeTab} onValueChange={setActiveTab} salonCount={salonsData?.total ?? 0} leadsCount={leads?.length ?? 0} pendingChannelCount={pendingChannelCount} />

          {/* Aba Métricas */}
          <TabsContent value="metrics">
            {metricsLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {Array.from({ length: 6 }).map((_, i) => (
                  <Card key={i} className="animate-pulse"><CardContent className="p-6"><div className="h-24 bg-muted rounded" /></CardContent></Card>
                ))}
              </div>
            ) : metrics ? (
              <div className="space-y-6">
                {/* KPI Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card className="border-l-4 border-l-emerald-500">
                    <CardContent className="p-5">
                      <div className="flex items-center gap-2 mb-1">
                        <DollarSign className="w-4 h-4 text-emerald-500" />
                        <span className="text-xs text-muted-foreground">MRR</span>
                      </div>
                      <div className="text-2xl font-semibold text-foreground">R$ {metrics.mrr.toFixed(0)}</div>
                      <div className="text-xs text-muted-foreground mt-1">receita mensal recorrente</div>
                    </CardContent>
                  </Card>
                  <Card className="border-l-4 border-l-blue-500">
                    <CardContent className="p-5">
                      <div className="flex items-center gap-2 mb-1">
                        <Building2 className="w-4 h-4 text-blue-500" />
                        <span className="text-xs text-muted-foreground">Salões Ativos</span>
                      </div>
                      <div className="text-2xl font-semibold text-foreground">{metrics.activeSalons}</div>
                      <div className="text-xs text-muted-foreground mt-1">{metrics.trialSalons} em trial</div>
                    </CardContent>
                  </Card>
                  <Card className="border-l-4 border-l-violet-500">
                    <CardContent className="p-5">
                      <div className="flex items-center gap-2 mb-1">
                        <Activity className="w-4 h-4 text-violet-500" />
                        <span className="text-xs text-muted-foreground">Agendamentos/mês</span>
                      </div>
                      <div className="text-2xl font-semibold text-foreground">{metrics.appointmentsThisMonth}</div>
                      <div className="text-xs text-muted-foreground mt-1">R$ {metrics.revenueThisMonth.toFixed(0)} gerado</div>
                    </CardContent>
                  </Card>
                  <Card className="border-l-4 border-l-amber-500">
                    <CardContent className="p-5">
                      <div className="flex items-center gap-2 mb-1">
                        <MessageCircle className="w-4 h-4 text-amber-500" />
                        <span className="text-xs text-muted-foreground">Canais Pendentes</span>
                      </div>
                      <div className="text-2xl font-semibold text-foreground">{metrics.channelRequestsPending}</div>
                      <div className="text-xs text-muted-foreground mt-1">{metrics.channelRequestsDone} concluídos</div>
                    </CardContent>
                  </Card>
                </div>

                {/* Gráficos */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Crescimento de Salões */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base font-medium flex items-center gap-2">
                        <TrendingUp className="w-4 h-4 text-primary" />
                        Novos Salões (6 meses)
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={200}>
                        <BarChart data={metrics.salonGrowthChart}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                          <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                          <Tooltip formatter={(v) => [`${v} salões`, "Novos"]} />
                          <Bar dataKey="count" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  {/* Salões por Plano */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base font-medium flex items-center gap-2">
                        <Crown className="w-4 h-4 text-amber-500" />
                        Salões por Plano
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {metrics.salonsByPlan.length > 0 ? (
                        <ResponsiveContainer width="100%" height={200}>
                          <PieChart>
                            <Pie
                              data={metrics.salonsByPlan}
                              dataKey="count"
                              nameKey="planName"
                              cx="50%"
                              cy="50%"
                              outerRadius={80}
                              label={({ planName, count }) => `${planName}: ${count}`}
                            >
                              {metrics.salonsByPlan.map((_, idx) => (
                                <Cell key={idx} fill={["#6366f1", "#f59e0b", "#10b981", "#3b82f6", "#ec4899"][idx % 5]} />
                              ))}
                            </Pie>
                            <Tooltip />
                          </PieChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="h-[200px] flex items-center justify-center text-muted-foreground text-sm">Nenhum salão cadastrado ainda</div>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Solicitações de Canal */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base font-medium">Status das Solicitações de Canal</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="text-center p-4 bg-amber-50 rounded-lg border border-amber-100">
                        <div className="text-2xl font-semibold text-amber-700">{metrics.channelRequestsPending}</div>
                        <div className="text-xs text-amber-600 mt-1">Aguardando</div>
                      </div>
                      <div className="text-center p-4 bg-blue-50 rounded-lg border border-blue-100">
                        <div className="text-2xl font-semibold text-blue-700">{metrics.channelRequestsInProgress}</div>
                        <div className="text-xs text-blue-600 mt-1">Em Andamento</div>
                      </div>
                      <div className="text-center p-4 bg-green-50 rounded-lg border border-green-100">
                        <div className="text-2xl font-semibold text-green-700">{metrics.channelRequestsDone}</div>
                        <div className="text-xs text-green-600 mt-1">Concluídos</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Métricas de Agentes IA */}
                {agentMetrics && (
                  <div className="space-y-4">
                    <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide flex items-center gap-2">
                      <Bot className="w-4 h-4 text-primary" />
                      Agentes de IA Nativos
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                      <Card className="border-l-4 border-l-primary">
                        <CardContent className="p-5">
                          <div className="flex items-center gap-2 mb-1">
                            <Bot className="w-4 h-4 text-primary" />
                            <span className="text-xs text-muted-foreground">Agentes Ativos</span>
                          </div>
                          <div className="text-2xl font-semibold text-foreground">{agentMetrics.activeAgents}</div>
                          <div className="text-xs text-muted-foreground mt-1">{agentMetrics.totalAgents} total</div>
                        </CardContent>
                      </Card>
                      <Card className="border-l-4 border-l-cyan-500">
                        <CardContent className="p-5">
                          <div className="flex items-center gap-2 mb-1">
                            <MessageCircle className="w-4 h-4 text-cyan-500" />
                            <span className="text-xs text-muted-foreground">Convers. Hoje</span>
                          </div>
                          <div className="text-2xl font-semibold text-foreground">{agentMetrics.sessionsToday}</div>
                          <div className="text-xs text-muted-foreground mt-1">{agentMetrics.sessionsThisWeek} esta semana</div>
                        </CardContent>
                      </Card>
                      <Card className="border-l-4 border-l-indigo-500">
                        <CardContent className="p-5">
                          <div className="flex items-center gap-2 mb-1">
                            <Activity className="w-4 h-4 text-indigo-500" />
                            <span className="text-xs text-muted-foreground">Total Mensagens</span>
                          </div>
                          <div className="text-2xl font-semibold text-foreground">{agentMetrics.totalMessages}</div>
                          <div className="text-xs text-muted-foreground mt-1">{agentMetrics.totalSessions} sessões</div>
                        </CardContent>
                      </Card>
                      <Card className="border-l-4 border-l-orange-400">
                        <CardContent className="p-5">
                          <div className="flex items-center gap-2 mb-1">
                            <Clock className="w-4 h-4 text-orange-400" />
                            <span className="text-xs text-muted-foreground">Tempo Médio</span>
                          </div>
                          <div className="text-2xl font-semibold text-foreground">
                            {agentMetrics.avgResponseMs > 0 ? `${(agentMetrics.avgResponseMs / 1000).toFixed(1)}s` : "—"}
                          </div>
                          <div className="text-xs text-muted-foreground mt-1">por resposta</div>
                        </CardContent>
                      </Card>
                      <Card className="border-l-4 border-l-violet-500" title="Referência de custo do modelo para respostas com tarifa verificável. Não representa cobrança da plataforma.">
                        <CardContent className="p-5">
                          <div className="flex items-center gap-2 mb-1">
                            <Activity className="w-4 h-4 text-violet-500" />
                            <span className="text-xs text-muted-foreground">Custo IA (ref.)</span>
                          </div>
                          <div className="text-2xl font-semibold text-foreground">
                            {Number(agentMetrics.totalEstimatedCostUsd ?? 0).toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 4, maximumFractionDigits: 6 })}
                          </div>
                          <div className="text-xs text-muted-foreground mt-1">
                            {agentMetrics.pricedAiResponses ?? 0} resposta(s) com tarifa verificada
                          </div>
                        </CardContent>
                      </Card>
                    </div>

                    {/* Top agentes */}
                    {agentMetrics.topAgents.length > 0 && (
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-base font-medium flex items-center gap-2">
                            <Sparkles className="w-4 h-4 text-primary" />
                            Top Agentes por Conversas
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-2">
                            {agentMetrics.topAgents.map((a: any, idx: number) => (
                              <div key={a.agentId} className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/40">
                                <span className="text-xs font-mono text-muted-foreground w-5">{idx + 1}.</span>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium truncate">{a.agentName}</p>
                                  <p className="text-xs text-muted-foreground">Salão #{a.salonId}</p>
                                </div>
                                <div className="text-right">
                                  <p className="text-sm font-semibold">{a.sessions}</p>
                                  <p className="text-xs text-muted-foreground">sessões</p>
                                </div>
                                <div className="text-right">
                                  <p className="text-sm font-semibold">{a.messages}</p>
                                  <p className="text-xs text-muted-foreground">msgs</p>
                                </div>
                                <Link href="/superadmin/agentes">
                                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                                    <Eye className="w-3.5 h-3.5" />
                                  </Button>
                                </Link>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <Card><CardContent className="p-8 text-center text-muted-foreground">Não foi possível carregar as métricas.</CardContent></Card>
            )}
          </TabsContent>

          <TabsContent value="salons">
            <Card>
              <CardHeader>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <CardTitle className="text-lg font-medium">
                    Gerenciar Salões
                    {salonsData && <span className="text-sm font-normal text-muted-foreground ml-2">({salonsData.total} total)</span>}
                  </CardTitle>
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="relative">
                      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        placeholder="Buscar por nome, slug ou telefone..."
                        value={salonSearch}
                        onChange={(e) => { setSalonSearch(e.target.value); setSalonPage(1); }}
                        className="pl-9 w-64"
                      />
                    </div>
                    <Select
                      value={salonStatus ?? "all"}
                      onValueChange={(v) => { setSalonStatus(v === "all" ? undefined : v as typeof salonStatus); setSalonPage(1); }}
                    >
                      <SelectTrigger className="w-36">
                        <SelectValue placeholder="Todos os status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        <SelectItem value="trial">Trial</SelectItem>
                        <SelectItem value="active">Ativo</SelectItem>
                        <SelectItem value="suspended">Suspenso</SelectItem>
                        <SelectItem value="cancelled">Cancelado</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select
                      value={salonPlanFilter}
                      onValueChange={(v) => { setSalonPlanFilter(v); setSalonPage(1); }}
                    >
                      <SelectTrigger className="w-40">
                        <Filter className="w-3.5 h-3.5 mr-1.5 text-muted-foreground" />
                        <SelectValue placeholder="Todos os planos" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos os planos</SelectItem>
                        <SelectItem value="none">Sem plano</SelectItem>
                        {plansList?.map((p: any) => (
                          <SelectItem key={p.id} value={String(p.id)}>{p.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button variant="outline" size="sm" onClick={() => utils.superAdmin.listSalons.invalidate()}>
                      <RefreshCw className="w-4 h-4" />
                    </Button>
                    <Button size="sm" onClick={() => setShowCreateSalon(v => !v)} className="gap-1.5">
                      <Plus className="w-4 h-4" />
                      Novo Salão
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* ─── Formulário de Criar Salão ─── */}
                {showCreateSalon && (
                  <div className="mb-6 p-5 border rounded-xl bg-muted/30">
                    <h3 className="font-semibold text-base mb-4 flex items-center gap-2"><Building2 className="w-4 h-4" /> Cadastrar Novo Salão</h3>
                    <form onSubmit={handleCreateSalon} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <label className="text-sm font-medium">Nome do Responsável *</label>
                        <Input placeholder="Ex: João Silva" value={newSalonForm.ownerName} onChange={e => setNewSalonForm(f => ({...f, ownerName: e.target.value}))} required />
                      </div>
                      <div className="space-y-1">
                        <label className="text-sm font-medium">E-mail do Responsável *</label>
                        <Input type="email" placeholder="joao@email.com" value={newSalonForm.ownerEmail} onChange={e => setNewSalonForm(f => ({...f, ownerEmail: e.target.value}))} required />
                      </div>
                      <div className="space-y-1">
                        <label className="text-sm font-medium">Nome do Salão *</label>
                        <Input placeholder="Ex: Studio Beleza" value={newSalonForm.name} onChange={e => handleNameChange(e.target.value)} required />
                      </div>
                      <div className="space-y-1">
                        <label className="text-sm font-medium">Slug (URL) *</label>
                        <div className="relative">
                          <Input
                            placeholder="studio-beleza"
                            value={newSalonForm.slug}
                            onChange={e => handleSlugChange(e.target.value)}
                            required
                            className={slugToCheck.length >= 2 && !slugChecking
                              ? slugCheck?.available ? "border-green-400 pr-8" : "border-red-400 pr-8"
                              : ""}
                          />
                          {slugToCheck.length >= 2 && (
                            <span className="absolute right-2 top-1/2 -translate-y-1/2">
                              {slugChecking
                                ? <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                                : slugCheck?.available
                                  ? <CheckCircle2 className="w-4 h-4 text-green-500" />
                                  : <XCircle className="w-4 h-4 text-red-500" />}
                            </span>
                          )}
                        </div>
                        <div className="text-xs">
                          {slugToCheck.length >= 2 && !slugChecking && slugCheck && (
                            slugCheck.available
                              ? <span className="text-green-600 font-medium">Slug disponível!</span>
                              : <span className="text-red-600">Já em uso.{slugCheck.suggestion ? <> Sugestão: <button type="button" className="underline font-medium" onClick={() => handleSlugChange(slugCheck.suggestion)}>{slugCheck.suggestion}</button></> : ""}</span>
                          )}
                          {!(slugToCheck.length >= 2 && !slugChecking && slugCheck) && (
                            <span className="text-muted-foreground">belezaebronze.club/agendar/{newSalonForm.slug || 'slug'}</span>
                          )}
                        </div>
                      </div>
                      <div className="space-y-1">
                        <label className="text-sm font-medium">Telefone</label>
                        <Input placeholder="(75) 99999-9999" value={newSalonForm.phone} onChange={e => setNewSalonForm(f => ({...f, phone: e.target.value}))} />
                      </div>
                      <div className="space-y-1">
                        <label className="text-sm font-medium">E-mail do Salão</label>
                        <Input type="email" placeholder="contato@salon.com" value={newSalonForm.email} onChange={e => setNewSalonForm(f => ({...f, email: e.target.value}))} />
                      </div>
                      <div className="space-y-1 md:col-span-2">
                        <label className="text-sm font-medium">Endereço</label>
                        <Input placeholder="Rua Exemplo, 123, Cidade - UF" value={newSalonForm.address} onChange={e => setNewSalonForm(f => ({...f, address: e.target.value}))} />
                      </div>
                      <div className="space-y-1 md:col-span-2">
                        <label className="text-sm font-medium">Descrição</label>
                        <Input placeholder="Descrição breve do salão" value={newSalonForm.description} onChange={e => setNewSalonForm(f => ({...f, description: e.target.value}))} />
                      </div>
                      <div className="space-y-1">
                        <label className="text-sm font-medium">Plano</label>
                        <Select value={newSalonForm.planId} onValueChange={v => setNewSalonForm(f => ({...f, planId: v}))}>
                          <SelectTrigger><SelectValue placeholder="Selecionar plano" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Sem plano</SelectItem>
                            {plansList?.map((p: any) => <SelectItem key={p.id} value={String(p.id)}>{p.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <label className="text-sm font-medium">Status inicial</label>
                        <Select value={newSalonForm.status} onValueChange={v => setNewSalonForm(f => ({...f, status: v as any}))}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="trial">Trial</SelectItem>
                            <SelectItem value="active">Ativo</SelectItem>
                            <SelectItem value="suspended">Suspenso</SelectItem>
                            <SelectItem value="cancelled">Cancelado</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {newSalonForm.status === "trial" && (
                        <div className="space-y-1">
                          <label className="text-sm font-medium">Dias de Trial</label>
                          <Input type="number" min={0} max={365} value={newSalonForm.trialDays} onChange={e => setNewSalonForm(f => ({...f, trialDays: Number(e.target.value)}))} />
                        </div>
                      )}
                      <div className="md:col-span-2 flex gap-2 justify-end pt-2">
                        <Button type="button" variant="outline" onClick={() => setShowCreateSalon(false)}>Cancelar</Button>
                        <Button type="submit" disabled={isCreateSalonBlocked} className="gap-1.5">
                          {createSalonMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                          Criar Salão
                        </Button>
                      </div>
                    </form>
                  </div>
                )}
                {/* Modal de Edição de Salão */}
                {editingSalon && (
                  <div className="border rounded-lg p-5 bg-muted/30 mb-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold text-base flex items-center gap-2"><Pencil className="w-4 h-4" /> Editar Salão: {editingSalon.name}</h3>
                      <Button variant="ghost" size="sm" onClick={() => setEditingSalon(null)}>✕</Button>
                    </div>
                    <form onSubmit={handleUpdateSalon} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <Label className="text-xs">Nome do Salão *</Label>
                        <Input value={editSalonForm.name} onChange={e => setEditSalonForm(p => ({ ...p, name: e.target.value }))} required />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Slug (URL) *</Label>
                        <Input value={editSalonForm.slug} onChange={e => setEditSalonForm(p => ({ ...p, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') }))} required />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Telefone</Label>
                        <Input value={editSalonForm.phone} onChange={e => setEditSalonForm(p => ({ ...p, phone: e.target.value }))} />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">E-mail do Salão</Label>
                        <Input type="email" value={editSalonForm.email} onChange={e => setEditSalonForm(p => ({ ...p, email: e.target.value }))} />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Endereço</Label>
                        <Input value={editSalonForm.address} onChange={e => setEditSalonForm(p => ({ ...p, address: e.target.value }))} />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Plano</Label>
                        <Select value={editSalonForm.planId} onValueChange={v => setEditSalonForm(p => ({ ...p, planId: v }))}>
                          <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
                          <SelectContent>
                                <SelectItem value="none">Sem plano</SelectItem>
                            {(plansList ?? []).map((pl: any) => (
                              <SelectItem key={pl.id} value={String(pl.id)}>{pl.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Status</Label>
                        <Select value={editSalonForm.status} onValueChange={v => setEditSalonForm(p => ({ ...p, status: v as any }))}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="trial">Trial</SelectItem>
                            <SelectItem value="active">Ativo</SelectItem>
                            <SelectItem value="suspended">Suspenso</SelectItem>
                            <SelectItem value="cancelled">Cancelado</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Taxa de implantação (R$)</Label>
                        <Input type="number" min="0" step="0.01" value={editSalonForm.contractImplementationFee} onChange={e => setEditSalonForm(p => ({ ...p, contractImplementationFee: e.target.value }))} />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Assinatura mensal (R$)</Label>
                        <Input type="number" min="0" step="0.01" value={editSalonForm.contractMonthlyFee} onChange={e => setEditSalonForm(p => ({ ...p, contractMonthlyFee: e.target.value }))} />
                      </div>
                      <div className="space-y-1 md:col-span-2">
                        <Label className="text-xs">Perfil do Painel de Chamada</Label>
                        <Select value={editSalonForm.queuePanelProfile} onValueChange={value => setEditSalonForm(p => ({ ...p, queuePanelProfile: value }))}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="tanning">Clínica de bronzeamento · alerta em 25 min</SelectItem>
                            <SelectItem value="salon">Salão de beleza · alerta em 15 min</SelectItem>
                            <SelectItem value="advanced_aesthetics">Estética avançada · alerta em 20 min</SelectItem>
                            <SelectItem value="barbershop">Barbearia · alerta em 10 min</SelectItem>
                            <SelectItem value="store">Loja de produtos · painel de venda do catálogo</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">Ajusta a linguagem do painel interno, público e dos alertas de preparação. Em Loja de produtos, o painel exibe a vitrine do catálogo.</p>
                      </div>
                      <div className="md:col-span-2 rounded-lg border border-primary/20 bg-primary/5 px-3 py-2 text-xs text-muted-foreground">
                        Anualidade das mensalidades: <strong className="text-foreground">R$ {(Number(editSalonForm.contractMonthlyFee || 0) * 12).toFixed(2).replace(".", ",")}</strong> · Primeiro ano com implantação: <strong className="text-foreground">R$ {(Number(editSalonForm.contractImplementationFee || 0) + Number(editSalonForm.contractMonthlyFee || 0) * 12).toFixed(2).replace(".", ",")}</strong>
                      </div>
                      <div className="space-y-1 md:col-span-2">
                        <Label className="text-xs">Descrição</Label>
                        <Textarea value={editSalonForm.description} onChange={e => setEditSalonForm(p => ({ ...p, description: e.target.value }))} rows={2} />
                      </div>
                      <div className="md:col-span-2 space-y-4 rounded-lg border border-primary/20 bg-primary/5 p-4">
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div className="flex items-center gap-2">
                            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary"><Wifi className="h-4 w-4" /></span>
                            <div>
                              <h4 className="text-sm font-semibold">WhatsApp via Evolution API</h4>
                              <p className="text-xs text-muted-foreground">Crie a instância, conecte pelo QR Code e registre o webhook deste salão.</p>
                            </div>
                          </div>
                          {evolutionStatus && (
                            <Badge className={evolutionStatus.connected ? "border-emerald-500/30 bg-emerald-500/15 text-emerald-700" : evolutionStatus.state === "not_configured" ? "border-border bg-muted text-muted-foreground" : "border-amber-500/30 bg-amber-500/15 text-amber-700"}>
                              {evolutionStatus.connected ? "Conectado" : evolutionStatus.state === "not_configured" ? "Não configurado" : evolutionStatus.state === "connecting" ? "Aguardando conexão" : "Desconectado"}
                            </Badge>
                          )}
                        </div>
                        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                          <div className="space-y-1.5">
                            <Label className="text-xs">URL da Evolution API</Label>
                            <Input placeholder="https://evolution.exemplo.com" value={evolutionForm.evolutionApiUrl} onChange={e => setEvolutionForm(form => ({ ...form, evolutionApiUrl: e.target.value }))} />
                          </div>
                          <div className="space-y-1.5">
                            <Label className="text-xs">API Key</Label>
                            <div className="relative">
                              <Input type={showEvolutionApiKey ? "text" : "password"} placeholder="Informe para criar ou alterar" value={evolutionForm.evolutionApiKey} onChange={e => setEvolutionForm(form => ({ ...form, evolutionApiKey: e.target.value }))} className="pr-9" />
                              <button type="button" className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" onClick={() => setShowEvolutionApiKey(show => !show)} aria-label={showEvolutionApiKey ? "Ocultar API Key" : "Mostrar API Key"}>
                                {showEvolutionApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                              </button>
                            </div>
                          </div>
                          <div className="space-y-1.5">
                            <Label className="text-xs">Nome da instância</Label>
                            <Input placeholder={`salon-${editingSalon.id}`} value={evolutionForm.instanceName} onChange={e => setEvolutionForm(form => ({ ...form, instanceName: e.target.value }))} className="font-mono" />
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <Button type="button" variant="outline" size="sm" className="gap-1.5" onClick={saveEvolutionConfiguration} disabled={updateEvolutionConfig.isPending || !evolutionForm.evolutionApiUrl || !evolutionForm.instanceName}>
                            {updateEvolutionConfig.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Salvar configuração
                          </Button>
                          <Button type="button" size="sm" className="gap-1.5" onClick={createOrConnectEvolutionInstance} disabled={createEvolutionInstance.isPending || !evolutionForm.evolutionApiUrl || !evolutionForm.instanceName || (!evolutionForm.evolutionApiKey && !editingSalon.evolutionApiKey)}>
                            {createEvolutionInstance.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <QrCode className="h-3.5 w-3.5" />} Criar instância / gerar QR
                          </Button>
                          <Button type="button" variant="ghost" size="sm" className="gap-1.5" onClick={() => testEvolutionConfig.mutate({ salonId: editingSalon.id })} disabled={testEvolutionConfig.isPending}>
                            <RefreshCw className={`h-3.5 w-3.5 ${testEvolutionConfig.isPending ? "animate-spin" : ""}`} /> Atualizar status
                          </Button>
                        </div>
                        {evolutionQr && (
                          <div className="flex flex-col items-center gap-3 rounded-lg border bg-white p-4">
                            <p className="text-sm font-medium text-gray-800">Escaneie com o WhatsApp do salão</p>
                            <img src={evolutionQr.startsWith("data:") ? evolutionQr : `data:image/png;base64,${evolutionQr}`} alt="QR Code para conexão do WhatsApp" className="h-48 w-48 object-contain" />
                            {evolutionQrText && <p className="max-w-md break-all text-center font-mono text-[10px] text-gray-500">{evolutionQrText}</p>}
                            <Button type="button" variant="outline" size="sm" className="gap-1.5" onClick={createOrConnectEvolutionInstance} disabled={createEvolutionInstance.isPending}><RefreshCw className="h-3.5 w-3.5" /> Atualizar QR</Button>
                          </div>
                        )}
                        <div className="space-y-1.5 border-t border-primary/15 pt-3">
                          <Label className="text-xs">Webhook do agente</Label>
                          <div className="flex items-center gap-2">
                            <Input value={`${window.location.origin}/api/agent/webhook/${editingSalon.id}`} readOnly className="bg-background font-mono text-xs" />
                            <Button type="button" variant="outline" size="icon" className="h-9 w-9 shrink-0" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/api/agent/webhook/${editingSalon.id}`); toast.success("URL do webhook copiada!"); }} aria-label="Copiar URL do webhook"><Copy className="h-3.5 w-3.5" /></Button>
                          </div>
                          <div className="flex flex-wrap items-center gap-2">
                            <Button type="button" variant="ghost" size="sm" className="gap-1.5 text-muted-foreground" onClick={() => setEvolutionWebhook.mutate({ salonId: editingSalon.id, webhookBaseUrl: window.location.origin })} disabled={setEvolutionWebhook.isPending || !evolutionForm.evolutionApiUrl || !evolutionForm.instanceName}>
                              {setEvolutionWebhook.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Link2 className="h-3.5 w-3.5" />} Configurar webhook automaticamente
                            </Button>
                            {configuredWebhookUrl && <span className="text-xs text-emerald-700">Webhook ativo: {configuredWebhookUrl}</span>}
                          </div>
                        </div>
                      </div>
                      <div className="md:col-span-2 flex gap-2 justify-end pt-2">
                        <Button type="button" variant="outline" onClick={() => setEditingSalon(null)}>Cancelar</Button>
                        <Button type="submit" disabled={updateSalonMutation.isPending} className="gap-1.5">
                          {updateSalonMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                          Salvar Alterações
                        </Button>
                      </div>
                    </form>
                  </div>
                )}
                {salonsLoading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-primary" />
                  </div>
                ) : filteredSalons.length === 0 ? (
                  <div className="text-center py-12">
                    <Building2 className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground">
                      {search ? "Nenhum salão encontrado." : "Nenhum salão cadastrado ainda."}
                    </p>
                  </div>
                ) : (
                  <>
                  <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Salão</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Plano</TableHead>
                        <TableHead>Cadastro</TableHead>
                        <TableHead>Trial até</TableHead>
                        <TableHead className="text-right">Ações</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredSalons.map((salon: any) => (
                        <TableRow key={salon.id}>
                          <TableCell>
                            <div>
                              <div className="font-medium text-foreground">{salon.name}</div>
                              <div className="text-xs text-muted-foreground">/{salon.slug}</div>
                              {salon.phone && <div className="text-xs text-muted-foreground">{salon.phone}</div>}
                            </div>
                          </TableCell>
                          <TableCell>{STATUS_BADGE[salon.status] ?? <Badge variant="outline">{salon.status}</Badge>}</TableCell>
                          <TableCell>
                            <span className="text-sm text-muted-foreground">
                              {salon.planId ? `Plano #${salon.planId}` : "—"}
                            </span>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {safeFormat(salon.createdAt, "dd/MM/yyyy")}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {safeFormat(salon.trialEndsAt, "dd/MM/yyyy")}
                          </TableCell>
                          <TableCell className="text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm">
                                  <MoreVertical className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem asChild>
                                  <Link href={`/superadmin/saloes/${salon.id}`}>
                                    <Eye className="w-4 h-4 mr-2" />
                                    Ver Detalhes
                                  </Link>
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleEditSalon(salon)}>
                                  <Pencil className="w-4 h-4 mr-2" />
                                  Editar Salão
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => {
                                    const adminUrl = `/admin?impersonate=${salon.id}`;
                                    window.open(adminUrl, '_blank');
                                    toast.info(`Abrindo painel do salão "${salon.name}" em nova aba...`);
                                  }}
                                  className="text-blue-600"
                                >
                                  <LogIn className="w-4 h-4 mr-2" />
                                  Acessar como Admin
                                </DropdownMenuItem>
                                {salon.status !== "active" && (
                                  <DropdownMenuItem
                                    onClick={() => activateSalon.mutate({ salonId: salon.id })}
                                    className="text-green-600"
                                  >
                                    <CheckCircle2 className="w-4 h-4 mr-2" />
                                    Ativar
                                  </DropdownMenuItem>
                                )}
                                {salon.status !== "suspended" && (
                                  <DropdownMenuItem
                                    onClick={() => suspendSalon.mutate({ salonId: salon.id })}
                                    className="text-orange-600"
                                  >
                                    <PauseCircle className="w-4 h-4 mr-2" />
                                    Suspender
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem
                                  onClick={() => resendWelcomeEmail.mutate({ salonId: salon.id })}
                                  disabled={resendWelcomeEmail.isPending}
                                >
                                  {resendWelcomeEmail.isPending
                                    ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    : <MailCheck className="w-4 h-4 mr-2" />}
                                  Reenviar E-mail de Boas-vindas
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => sendMonthlyReport.mutate({ salonId: salon.id })}
                                  disabled={sendMonthlyReport.isPending}
                                >
                                  {sendMonthlyReport.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <SendHorizontal className="w-4 h-4 mr-2" />}
                                  Enviar Relatório Mensal
                                </DropdownMenuItem>
                                {salon.status !== "cancelled" && (
                                  <DropdownMenuItem
                                    onClick={() => {
                                      if (confirm(`Cancelar o salão "${salon.name}"? Esta ação não pode ser desfeita.`)) {
                                        cancelSalon.mutate({ salonId: salon.id });
                                      }
                                    }}
                                    className="text-red-600"
                                  >
                                    <XCircle className="w-4 h-4 mr-2" />
                                    Cancelar
                                  </DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  </div>
                  {/* Paginação */}
                  {salonsData && salonsData.totalPages > 1 && (
                    <div className="flex items-center justify-between mt-4 pt-4 border-t">
                      <p className="text-sm text-muted-foreground">
                        Página {salonsData.page} de {salonsData.totalPages} &mdash; {salonsData.total} salões
                      </p>
                      <div className="flex gap-2">
                        <Button
                          variant="outline" size="sm"
                          disabled={salonPage <= 1}
                          onClick={() => setSalonPage(p => Math.max(1, p - 1))}
                        >
                          Anterior
                        </Button>
                        <Button
                          variant="outline" size="sm"
                          disabled={salonPage >= salonsData.totalPages}
                          onClick={() => setSalonPage(p => p + 1)}
                        >
                          Próxima
                        </Button>
                      </div>
                    </div>
                  )}
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="access">
            <SuperAdminAccessCenter
              overview={accessOverview}
              isLoading={accessOverviewLoading}
              search={accessSearch}
              onSearchChange={setAccessSearch}
              isUpdating={setStaffAccessStatus.isPending || forceLogout.isPending}
              onToggleStatus={(member: any) => {
                const request = requestStaffAccessStatusChange(member, window.prompt, window.confirm);
                if (request) setStaffAccessStatus.mutate(request);
              }}
              onForceLogout={(member: any) => {
                const request = requestUserSessionLogout(member, window.prompt, window.confirm);
                if (request) forceLogout.mutate(request);
              }}
            />
          </TabsContent>

          <TabsContent value="platform-access">
            <PlatformAdminAccessPanel />
          </TabsContent>

          <TabsContent value="success">
            <SalonSuccessCockpit healthOverview={salonHealthOverview} billingOverview={billingOverview} onboardingOverview={salonOnboardingOverview} integrationOverview={integrationOverview} isLoading={salonHealthLoading || billingLoading || onboardingLoading || integrationLoading} onOpenTab={setActiveTab} />
          </TabsContent>

          <TabsContent value="execution">
            <PlatformExecutionCenter healthOverview={salonHealthOverview} billingOverview={billingOverview} onboardingOverview={salonOnboardingOverview} integrationOverview={integrationOverview} isLoading={salonHealthLoading || billingLoading || onboardingLoading || integrationLoading} onOpenTab={setActiveTab} />
          </TabsContent>

          <TabsContent value="health">
            <SalonHealthPanel overview={salonHealthOverview} isLoading={salonHealthLoading} />
          </TabsContent>

          <TabsContent value="billing">
            <SalonBillingPanel overview={billingOverview} isLoading={billingLoading} onUpdate={(input: any) => updateSalonBilling.mutate(input)} isUpdating={updateSalonBilling.isPending} />
          </TabsContent>

          <TabsContent value="onboarding">
            <SalonOnboardingPanel overview={salonOnboardingOverview} isLoading={onboardingLoading} onUpdate={(input: any) => updateSalonOnboarding.mutate(input)} isUpdating={updateSalonOnboarding.isPending} />
          </TabsContent>

          <TabsContent value="reports">
            <PlatformExecutiveReportsPanel report={platformExecutiveReport} periodDays={reportPeriod} onPeriodChange={setReportPeriod} isLoading={reportsLoading} />
          </TabsContent>

          <TabsContent value="integrations">
            <SalonIntegrationPanel overview={integrationOverview} isLoading={integrationLoading} onCreateFollowup={(input) => createIntegrationFollowup.mutate(input)} onCompleteFollowup={(id) => completeIntegrationFollowup.mutate({ id })} isUpdating={createIntegrationFollowup.isPending || completeIntegrationFollowup.isPending} />
          </TabsContent>

          <TabsContent value="users">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-medium">Usuários Cadastrados</CardTitle>
                  <div className="relative w-64">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Buscar usuário..."
                      className="pl-9 h-9"
                      value={userSearch}
                      onChange={(e) => setUserSearch(e.target.value)}
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {usersLoading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-primary" />
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Usuário</TableHead>
                        <TableHead>E-mail</TableHead>
                        <TableHead>Telefone</TableHead>
                        <TableHead>Perfil</TableHead>
                        <TableHead>Cadastro</TableHead>
                        <TableHead className="text-right">Ações</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(users ?? [])
                        .filter(u => !userSearch ||
                          (u.name ?? "").toLowerCase().includes(userSearch.toLowerCase()) ||
                          (u.email ?? "").toLowerCase().includes(userSearch.toLowerCase())
                        )
                        .map((u: any) => (
                        <TableRow key={u.id}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-primary text-sm font-medium">
                                {(u.name ?? "U").charAt(0).toUpperCase()}
                              </div>
                              <span className="font-medium text-foreground">{u.name ?? "—"}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">{u.email ?? "—"}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">{(u as any).phone ?? "—"}</TableCell>
                          <TableCell>
                            {u.role === "superadmin" ? (
                              <Badge className="bg-purple-100 text-purple-700">Super Admin</Badge>
                            ) : u.role === "admin" ? (
                              <Badge className="bg-blue-100 text-blue-700">Admin Salão</Badge>
                            ) : (
                              <Badge variant="outline">Usuário</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {safeFormat(u.createdAt, "dd/MM/yyyy")}
                          </TableCell>
                          <TableCell className="text-right">
                            {u.role !== "superadmin" && (
                              <div className="flex items-center justify-end gap-1.5">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 text-xs text-amber-700 border-amber-200 hover:bg-amber-50"
                                  onClick={() => {
                                    setNewPasswordInput("");
                                    setShowNewPasswordInput(false);
                                    setSetPasswordModal({ userId: u.id, userName: u.name ?? u.email ?? "Usuário" });
                                  }}
                                >
                                  <Shield className="w-3 h-3 mr-1" />
                                  Definir Senha
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 text-xs text-red-600 border-red-200 hover:bg-red-50"
                                  onClick={() => {
                                    const reason = window.prompt(`Informe o motivo para encerrar a sessão de ${u.name ?? u.email}:`);
                                    if (reason?.trim() && confirm(`Forçar logout de "${u.name ?? u.email}"? O usuário precisará fazer login novamente.`)) {
                                      forceLogout.mutate({ userId: u.id, reason: reason.trim() });
                                    }
                                  }}
                                  disabled={forceLogout.isPending}
                                >
                                  <UserX className="w-3 h-3 mr-1" />
                                  Forçar Logout
                                </Button>
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Modal Definir Senha */}
          {setPasswordModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
              <div className="bg-background border border-border rounded-xl shadow-2xl w-full max-w-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-base flex items-center gap-2">
                    <Shield className="w-4 h-4 text-amber-500" />
                    Definir Senha
                  </h3>
                  <button
                    onClick={() => { setSetPasswordModal(null); setNewPasswordInput(""); }}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    ✕
                  </button>
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                  Definir nova senha para <strong>{setPasswordModal.userName}</strong>.
                </p>
                <div className="space-y-3">
                  <div className="relative">
                    <Input
                      type={showNewPasswordInput ? "text" : "password"}
                      placeholder="Nova senha (mínimo 6 caracteres)"
                      value={newPasswordInput}
                      onChange={(e) => setNewPasswordInput(e.target.value)}
                      className="pr-10"
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPasswordInput(!showNewPasswordInput)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showNewPasswordInput ? (
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                      ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                      )}
                    </button>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => { setSetPasswordModal(null); setNewPasswordInput(""); }}
                    >
                      Cancelar
                    </Button>
                    <Button
                      className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
                      disabled={newPasswordInput.length < 6 || adminSetPassword.isPending}
                      onClick={() => {
                        if (newPasswordInput.length < 6) {
                          toast.error("A senha deve ter pelo menos 6 caracteres");
                          return;
                        }
                        adminSetPassword.mutate({ userId: setPasswordModal.userId, newPassword: newPasswordInput });
                      }}
                    >
                      {adminSetPassword.isPending ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        "Salvar Senha"
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Aba Clientes */}
          <TabsContent value="clients">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <CardTitle className="text-lg font-medium">Clientes de Todos os Salões</CardTitle>
                  <div className="flex items-center gap-2">
                    <div className="relative w-56">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        placeholder="Buscar cliente..."
                        className="pl-9 h-9"
                        value={clientSearch}
                        onChange={(e) => { setClientSearch(e.target.value); setClientPage(1); }}
                      />
                    </div>
                    <Select
                      value={clientSalonFilter?.toString() ?? "all"}
                      onValueChange={(v) => { setClientSalonFilter(v === "all" ? null : Number(v)); setClientPage(1); }}
                    >
                      <SelectTrigger className="w-44 h-9">
                        <SelectValue placeholder="Todos os salões" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos os salões</SelectItem>
                        {(salonsData?.data ?? []).map((s: any) => (
                          <SelectItem key={s.id} value={s.id.toString()}>{s.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {clientsLoading ? (
                  <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
                ) : !(allClients as any)?.clients?.length ? (
                  <div className="text-center py-12">
                    <Users className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground">Nenhum cliente encontrado.</p>
                  </div>
                ) : (
                  <>
                    <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Cliente</TableHead>
                          <TableHead>Telefone</TableHead>
                          <TableHead>Salão</TableHead>
                          <TableHead>Visitas</TableHead>
                          <TableHead>Total Gasto</TableHead>
                          <TableHead>Última Visita</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {((allClients as any)?.clients ?? []).map((c: any, i: number) => (
                          <TableRow key={i}>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center text-amber-700 text-sm font-medium">
                                  {(c.clientName ?? "?").charAt(0).toUpperCase()}
                                </div>
                                <div>
                                  <p className="font-medium text-sm">{c.clientName ?? "—"}</p>
                                  {c.clientEmail && <p className="text-xs text-muted-foreground">{c.clientEmail}</p>}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">{c.clientPhone ?? "—"}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className="text-xs">{c.salonName}</Badge>
                            </TableCell>
                            <TableCell className="text-sm font-medium">{c.totalVisits}</TableCell>
                            <TableCell className="text-sm">
                              {c.totalSpent > 0 ? `R$ ${Number(c.totalSpent).toFixed(2)}` : "—"}
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {safeFormat(c.lastVisit ? Number(c.lastVisit) : null, "dd/MM/yyyy")}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    </div>
                    {(allClients as any)?.total > 30 && (
                      <div className="flex items-center justify-between mt-4 pt-4 border-t">
                        <p className="text-sm text-muted-foreground">Página {clientPage} — {(allClients as any).total} clientes</p>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" disabled={clientPage <= 1} onClick={() => setClientPage(p => p - 1)}>Anterior</Button>
                          <Button variant="outline" size="sm" disabled={(allClients as any).clients.length < 30} onClick={() => setClientPage(p => p + 1)}>Próxima</Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Aba Leads */}
          <TabsContent value="leads">
            <Card>
              <CardHeader>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <CardTitle className="text-lg font-medium">Leads Capturados</CardTitle>
                    <p className="mt-1 text-xs text-muted-foreground">Busque e entre em contato com os interessados sem sair do painel.</p>
                  </div>
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        value={leadSearch}
                        onChange={(event) => setLeadSearch(event.target.value)}
                        placeholder="Buscar lead..."
                        className="h-9 w-full pl-9 sm:w-52"
                      />
                    </div>
                    <Button
                      variant="outline" size="sm"
                      onClick={() => {
                        if (!filteredLeads.length) return;
                      const csv = ["Nome,E-mail,Telefone,Data"].concat(
                        filteredLeads.map((l: any) => `"${l.name ?? ""}","${l.email}","${l.phone ?? ""}","${safeFormat(l.createdAt, "dd/MM/yyyy HH:mm")}"`)
                      ).join("\n");
                      const blob = new Blob([csv], { type: "text/csv" });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement("a"); a.href = url; a.download = "leads-filtrados.csv"; a.click();
                    }}
                  >
                    Exportar CSV
                  </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {leadsLoading ? (
                  <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
                ) : !filteredLeads.length ? (
                  <div className="text-center py-12">
                    <Mail className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground">{leads?.length ? "Nenhum lead encontrado para a busca." : "Nenhum lead capturado ainda."}</p>
                    <p className="text-sm text-muted-foreground mt-1">Os leads aparecem aqui quando visitantes preenchem o formulário na landing page.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Nome</TableHead>
                        <TableHead>E-mail</TableHead>
                        <TableHead>Telefone</TableHead>
                        <TableHead>Data</TableHead>
                        <TableHead className="text-right">Contato</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredLeads.map((l: any) => {
                        const whatsappUrl = getLeadWhatsAppUrl(l.phone);
                        return (
                        <TableRow key={l.id}>
                          <TableCell className="font-medium">{l.name ?? "—"}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">{l.email}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">{l.phone ?? "—"}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {safeFormat(l.createdAt, "dd/MM/yyyy HH:mm")}
                          </TableCell>
                          <TableCell>
                            <div className="flex justify-end gap-1">
                              {whatsappUrl && (
                                <Button asChild variant="ghost" size="icon" className="h-8 w-8 text-green-600" title="Abrir WhatsApp">
                                  <a href={whatsappUrl} target="_blank" rel="noreferrer"><MessageCircle className="h-4 w-4" /></a>
                                </Button>
                              )}
                              {l.phone && (
                                <Button asChild variant="ghost" size="icon" className="h-8 w-8" title="Ligar para o lead">
                                  <a href={`tel:${l.phone}`}><Phone className="h-4 w-4" /></a>
                                </Button>
                              )}
                              {l.email && (
                                <Button asChild variant="ghost" size="icon" className="h-8 w-8" title="Enviar e-mail">
                                  <a href={`mailto:${l.email}`}><Mail className="h-4 w-4" /></a>
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      )})}
                    </TableBody>
                  </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          <TabsContent value="channels">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-medium">Solicitações de Conexão de Canal</CardTitle>
                  <Button variant="outline" size="sm" onClick={() => utils.superAdmin.listChannelRequests.invalidate()}>
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {channelLoading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-primary" />
                  </div>
                ) : !channelRequests || channelRequests.length === 0 ? (
                  <div className="text-center py-12">
                    <MessageCircle className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground">Nenhuma solicitação de conexão ainda.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {channelRequests.map((req: any) => (
                      <div key={req.id} className="border border-border/50 rounded-xl p-4 space-y-3">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-center gap-3">
                            {req.channelType === "whatsapp" ? (
                              <div className="w-10 h-10 rounded-full bg-green-500/15 flex items-center justify-center">
                                <MessageCircle className="w-5 h-5 text-green-600" />
                              </div>
                            ) : (
                              <div className="w-10 h-10 rounded-full bg-pink-500/15 flex items-center justify-center">
                                <Sparkles className="w-5 h-5 text-pink-600" />
                              </div>
                            )}
                            <div>
                              <p className="font-medium text-sm">{req.salonName}</p>
                              <p className="text-xs text-muted-foreground">
                                {req.channelType === "whatsapp" ? "WhatsApp" : "Instagram"} · {safeFormat(req.createdAt, "dd/MM/yyyy HH:mm")}
                              </p>
                            </div>
                          </div>
                          <Badge
                            className={
                              req.status === "done" ? "bg-green-500/15 text-green-600 border-green-500/30" :
                              req.status === "in_progress" ? "bg-blue-500/15 text-blue-600 border-blue-500/30" :
                              req.status === "cancelled" ? "bg-red-500/15 text-red-600 border-red-500/30" :
                              "bg-amber-500/15 text-amber-600 border-amber-500/30"
                            }
                          >
                            {req.status === "done" ? "Concluído" :
                             req.status === "in_progress" ? "Em andamento" :
                             req.status === "cancelled" ? "Cancelado" : "Aguardando"}
                          </Badge>
                        </div>

                        {/* Dados do contato */}
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          {req.contactName && (
                            <div className="flex items-center gap-1.5 text-muted-foreground">
                              <Users className="w-3.5 h-3.5" />
                              <span>{req.contactName}</span>
                            </div>
                          )}
                          {req.contactPhone && (
                            <div className="flex items-center gap-1.5 text-muted-foreground">
                              <Phone className="w-3.5 h-3.5" />
                              <span>{req.contactPhone}</span>
                            </div>
                          )}
                        </div>

                        {req.notes && (
                          <p className="text-xs text-muted-foreground bg-secondary/30 rounded-lg p-2">
                            <strong>Obs:</strong> {req.notes}
                          </p>
                        )}

                        {req.adminNotes && (
                          <p className="text-xs text-blue-700 dark:text-blue-400 bg-blue-500/10 rounded-lg p-2">
                            <strong>Nota interna:</strong> {req.adminNotes}
                          </p>
                        )}

                        {/* Painel de edição */}
                        {editingRequest?.id === req.id ? (
                          <div className="border-t border-border/50 pt-3 space-y-2">
                            <div>
                              <label className="text-xs font-medium mb-1 block">Status</label>
                              <Select
                                value={editingRequest?.status}
                                onValueChange={(v) => editingRequest && setEditingRequest({ ...editingRequest, status: v as "pending" | "in_progress" | "done" | "cancelled" })}
                              >
                                <SelectTrigger className="h-8 text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="pending">Aguardando</SelectItem>
                                  <SelectItem value="in_progress">Em andamento</SelectItem>
                                  <SelectItem value="done">Concluído</SelectItem>
                                  <SelectItem value="cancelled">Cancelado</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <label className="text-xs font-medium mb-1 block">Telefone de contato (visível ao salão)</label>
                              <Input
                                value={editingRequest?.adminContactPhone}
                                onChange={(e) => editingRequest && setEditingRequest({ ...editingRequest, adminContactPhone: e.target.value })}
                                className="h-8 text-xs"
                                placeholder="(11) 99999-9999"
                              />
                              <p className="text-xs text-muted-foreground mt-1">Será enviado na notificação para o salão</p>
                            </div>
                            <div>
                              <label className="text-xs font-medium mb-1 block">Nota interna</label>
                              <Textarea
                                value={editingRequest?.adminNotes}
                                onChange={(e) => editingRequest && setEditingRequest({ ...editingRequest, adminNotes: e.target.value })}
                                className="text-xs resize-none"
                                rows={2}
                                placeholder="Anotações internas sobre esta solicitação..."
                              />
                            </div>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                className="h-7 text-xs"
                                onClick={() => {
                                  if (!editingRequest) return;
                                  updateChannelRequest.mutate({
                                    id: editingRequest.id,
                                    status: editingRequest.status,
                                    adminNotes: editingRequest.adminNotes || undefined,
                                    adminContactPhone: editingRequest.adminContactPhone || undefined,
                                  });
                                }}
                                disabled={updateChannelRequest.isPending}
                              >
                                {updateChannelRequest.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : "Salvar"}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 text-xs"
                                onClick={() => setEditingRequest(null)}
                              >
                                Cancelar
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs w-full"
                                onClick={() => setEditingRequest({ id: req.id, status: req.status, adminNotes: req.adminNotes ?? "", adminContactPhone: req.adminContactPhone ?? "" })}
                          >
                            Gerenciar Solicitação
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Aba Funcionalidades */}
          <TabsContent value="features">
            <FeaturesTab salons={salonsData?.data ?? []} />
          </TabsContent>

          {/* Aba Auditoria detalhada */}
          <TabsContent value="audit">
            <DetailedAuditPanel overview={detailedAuditOverview} isLoading={detailedAuditLoading} />
          </TabsContent>

          {/* Aba Tutoriais — gerenciar URLs de vídeos */}
          <TabsContent value="tutorials">
            <TutorialVideosTab />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

// ─── Saúde dos Salões ──────────────────────────────────────────────────────────
function SuperAdminSalonHealth({ overview, isLoading }: any) {
  const [levelFilter, setLevelFilter] = useState<"all" | "healthy" | "attention" | "critical">("all");
  const summary = overview?.summary;
  const levelLabel: Record<string, string> = { healthy: "Saudável", attention: "Atenção", critical: "Crítico" };
  const levelClass: Record<string, string> = { healthy: "bg-emerald-100 text-emerald-800", attention: "bg-amber-100 text-amber-800", critical: "bg-red-100 text-red-800" };
  const salons = filterSalonHealth(overview?.salons ?? [], levelFilter);

  if (isLoading) return <Card><CardContent className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></CardContent></Card>;

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div><h2 className="text-lg font-semibold flex items-center gap-2"><Activity className="w-5 h-5 text-primary" /> Saúde dos Salões</h2><p className="text-sm text-muted-foreground">Priorize planos, atividade operacional, receita recente e riscos de acesso.</p></div>
        <div className="flex flex-wrap gap-2">{[["all", "Todos"], ["critical", "Crítico"], ["attention", "Atenção"], ["healthy", "Saudável"]].map(([value, label]) => <Button key={value} size="sm" variant={levelFilter === value ? "default" : "outline"} onClick={() => setLevelFilter(value as any)}>{label}</Button>)}</div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
        {buildSalonHealthCards(summary ?? {}).map(({ key, label, value }) => {
          const color = key === "healthy" ? "text-emerald-600" : key === "attention" ? "text-amber-600" : key === "critical" ? "text-red-600" : key === "appointments" ? "text-blue-600" : "text-foreground";
          const display = key === "revenue" ? new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 }).format(Number(value)) : value;
          return <Card key={key}><CardContent className="p-4"><p className="text-xs text-muted-foreground">{label}</p><p className={`text-2xl font-semibold mt-1 ${color}`}>{display}</p></CardContent></Card>;
        })}
      </div>
      <div className="grid gap-3 xl:grid-cols-2">
        {salons.map((salon: any) => <Card key={salon.id} className={salon.level === "critical" ? "border-red-200" : salon.level === "attention" ? "border-amber-200" : ""}>
          <CardContent className="p-5">
            <div className="flex gap-4 justify-between"><div><div className="flex items-center gap-2"><h3 className="font-semibold">{salon.name}</h3><Badge className={levelClass[salon.level]}>{levelLabel[salon.level]}</Badge></div><p className="text-xs text-muted-foreground mt-1">/{salon.slug} · Plano #{salon.planId ?? "—"} · {salon.status}</p></div><div className="w-12 h-12 rounded-full border-4 border-current flex items-center justify-center text-sm font-bold text-primary shrink-0">{salon.score}</div></div>
            <div className="grid grid-cols-3 gap-2 mt-4 text-center"><div className="rounded-lg bg-muted/60 p-2"><p className="text-xs text-muted-foreground">Agenda</p><p className="font-semibold">{salon.appointmentsLast30Days}</p></div><div className="rounded-lg bg-muted/60 p-2"><p className="text-xs text-muted-foreground">Concluídos</p><p className="font-semibold">{salon.completedAppointmentsLast30Days}</p></div><div className="rounded-lg bg-muted/60 p-2"><p className="text-xs text-muted-foreground">Receita</p><p className="font-semibold text-sm">{new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 }).format(salon.revenueLast30Days)}</p></div></div>
            <div className="mt-4"><p className="text-xs font-medium text-muted-foreground mb-1">Próxima ação recomendada</p>{salon.reasons.length ? <ul className="space-y-1">{salon.reasons.map((reason: string) => <li key={reason} className="text-sm flex gap-1.5"><AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />{reason}</li>)}</ul> : <p className="text-sm text-emerald-700 flex gap-1.5"><CheckCircle2 className="w-4 h-4 shrink-0" />Operação, acesso e assinatura sem alerta prioritário.</p>}</div>
          </CardContent>
        </Card>)}
      </div>
      {salons.length === 0 && <Card><CardContent className="p-8 text-center text-muted-foreground">Nenhum salão corresponde ao filtro de saúde selecionado.</CardContent></Card>}
    </div>
  );
}

// ─── Central de Acessos ────────────────────────────────────────────────────────
export function SuperAdminAccessCenter({ overview, isLoading, search, onSearchChange, onToggleStatus, onForceLogout, isUpdating }: any) {
  const [riskPriority, setRiskPriority] = useState<"all" | "critical" | "attention" | "informational">("all");
  const query = search.trim().toLowerCase();
  const salons = (overview?.salons ?? []).filter((salon: any) => {
    const matchesSearch = !query || [salon.name, salon.slug, salon.ownerName, salon.ownerEmail].filter(Boolean).some(value => String(value).toLowerCase().includes(query));
    const matchesPriority = riskPriority === "all" || salon.risks.some((risk: any) => risk.priority === riskPriority);
    return matchesSearch && matchesPriority;
  });
  const summary = overview?.summary;
  const priorityLabel: Record<string, string> = { critical: "Crítico", attention: "Atenção", informational: "Informativo" };
  const priorityClass: Record<string, string> = { critical: "bg-red-100 text-red-800", attention: "bg-amber-100 text-amber-800", informational: "bg-blue-100 text-blue-800" };

  if (isLoading) return <Card><CardContent className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></CardContent></Card>;

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2"><Shield className="w-5 h-5 text-primary" /> Central de Acessos</h2>
          <p className="text-sm text-muted-foreground">Visão por salão, riscos de vínculo e ações rápidas de sessão e status.</p>
        </div>
        <div className="relative w-full md:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input value={search} onChange={e => onSearchChange(e.target.value)} placeholder="Buscar salão ou responsável..." className="pl-9" />
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        {[
          ["Salões", summary?.salons ?? 0, "text-foreground"],
          ["Acessos ativos", summary?.activeAccounts ?? 0, "text-emerald-600"],
          ["Administradores", summary?.privilegedAccounts ?? 0, "text-blue-600"],
          ["Convites pendentes", summary?.pendingInvites ?? 0, "text-amber-600"],
          ["Sem vínculo", summary?.unlinkedAccounts ?? 0, "text-red-600"],
          ["Inativos há 45 dias", summary?.staleAccounts ?? 0, "text-orange-600"],
        ].map(([label, value, color]) => (
          <Card key={String(label)}><CardContent className="p-4"><p className="text-xs text-muted-foreground">{label}</p><p className={`text-2xl font-semibold mt-1 ${color}`}>{value}</p></CardContent></Card>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2 rounded-lg border bg-muted/30 p-3">
        <span className="text-sm font-medium mr-1">Prioridade de risco:</span>
        {[
          ["all", "Todos", (summary?.criticalRisks ?? 0) + (summary?.attentionRisks ?? 0) + (summary?.informationalRisks ?? 0)],
          ["critical", "Crítico", summary?.criticalRisks ?? 0],
          ["attention", "Atenção", summary?.attentionRisks ?? 0],
          ["informational", "Informativo", summary?.informationalRisks ?? 0],
        ].map(([value, label, count]) => <Button key={String(value)} size="sm" variant={riskPriority === value ? "default" : "outline"} onClick={() => setRiskPriority(value as any)}>{label} ({count})</Button>)}
      </div>

      {salons.map((salon: any) => (
        <Card key={salon.id} className={salon.risks.length ? "border-amber-200" : ""}>
          <CardHeader className="pb-3">
            <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
              <div>
                <CardTitle className="text-base flex items-center gap-2"><Building2 className="w-4 h-4 text-primary" />{salon.name}<Badge variant={salon.status === "active" ? "default" : "secondary"}>{salon.status}</Badge></CardTitle>
                <p className="text-xs text-muted-foreground mt-1">Responsável: {salon.ownerName ?? "Não vinculado"} · {salon.ownerEmail ?? "sem e-mail"}</p>
              </div>
              <div className="flex flex-wrap gap-1.5 text-xs">
                <Badge variant="outline">{salon.counts.active} ativos</Badge><Badge variant="outline">{salon.counts.admins} admins</Badge>
                {salon.risks.length > 0 && <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">{salon.risks.length} alertas</Badge>}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {salon.risks.length > 0 && <div className="mb-3 flex flex-wrap gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900"><strong className="mr-1">Alertas:</strong>{salon.risks.map((risk: any, index: number) => <span key={`${risk.staffId ?? "team"}-${index}`} className="inline-flex items-center gap-1"><Badge className={priorityClass[risk.priority] ?? priorityClass.informational}>{priorityLabel[risk.priority] ?? "Informativo"}</Badge>{risk.name}: {risk.type}</span>)}</div>}
            {salon.members.length === 0 ? <p className="text-sm text-muted-foreground py-2">Nenhum membro de equipe vinculado. Revise a criação de acessos deste salão.</p> : (
              <div className="overflow-x-auto"><Table><TableHeader><TableRow><TableHead>Conta</TableHead><TableHead>Papel</TableHead><TableHead>Vínculo</TableHead><TableHead>Último acesso</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Ações</TableHead></TableRow></TableHeader><TableBody>
                {salon.members.map((member: any) => <TableRow key={member.staffId}>
                  <TableCell><div><p className="font-medium text-sm">{member.userName ?? member.name}</p><p className="text-xs text-muted-foreground">{member.userEmail ?? member.email ?? "sem e-mail"}</p></div></TableCell>
                  <TableCell><Badge variant={member.privileged ? "default" : "outline"}>{member.isOwner ? "proprietário" : member.staffRole}{member.canAuthorize ? " · autorizador" : ""}</Badge></TableCell>
                  <TableCell className="text-sm">{member.professionalName ?? (member.staffRole === "funcionario" ? "Sem profissional" : "—")}</TableCell>
                  <TableCell className="text-sm">{member.lastSignedIn ? safeFormat(member.lastSignedIn, "dd/MM/yyyy HH:mm") : "Nunca"}</TableCell>
                  <TableCell><div className="flex flex-wrap gap-1"><Badge variant={member.isActive ? "outline" : "secondary"}>{member.isActive ? "Ativo" : "Suspenso"}</Badge>{member.pending && <Badge className="bg-amber-100 text-amber-800">Pendente</Badge>}{member.stale && <Badge className="bg-orange-100 text-orange-800">Inativo</Badge>}</div></TableCell>
                  <TableCell className="text-right"><div className="flex justify-end gap-1">{!member.isOwner && <Button size="sm" variant="outline" disabled={isUpdating} onClick={() => onToggleStatus(member)}>{member.isActive ? "Suspender" : "Reativar"}</Button>}{member.userId && <Button size="sm" variant="outline" disabled={isUpdating} onClick={() => onForceLogout(member)}><UserX className="w-3.5 h-3.5 mr-1" /> Sessão</Button>}</div></TableCell>
                </TableRow>)}
              </TableBody></Table></div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ─── Componente ImpersonationAuditTab ─────────────────────────────────────────
function ImpersonationAuditTab() {
  const { data: logs, isLoading } = (trpc as any).superAdmin.impersonationLogs.useQuery({ limit: 100 });

  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <Card key={i} className="animate-pulse">
            <CardContent className="p-4"><div className="h-10 bg-muted rounded" /></CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <History className="w-5 h-5 text-primary" />
          Log de Auditoria — Impersonation
        </h2>
        <p className="text-sm text-muted-foreground mt-0.5">
          Registro de todos os acessos de Super Admin a painéis de salões.
        </p>
      </div>

      {(!logs || logs.length === 0) ? (
        <Card>
          <CardContent className="p-8 text-center text-muted-foreground">
            <History className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm">Nenhum acesso de impersonation registrado ainda.</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data/Hora</TableHead>
                  <TableHead>Super Admin</TableHead>
                  <TableHead>Salão acessado</TableHead>
                  <TableHead>Duração</TableHead>
                  <TableHead>IP</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map((log: any) => {
                  const enteredAt = toValidDate(log.createdAt);
                  const exitedAt = toValidDate(log.exitedAt);
                  const durationMs = enteredAt && exitedAt ? exitedAt.getTime() - enteredAt.getTime() : null;
                  const durationStr = durationMs
                    ? durationMs < 60000
                      ? `${Math.round(durationMs / 1000)}s`
                      : `${Math.round(durationMs / 60000)}min`
                    : "Em andamento";

                  return (
                    <TableRow key={log.id}>
                      <TableCell className="text-xs">
                        {safeFormat(log.createdAt, "dd/MM/yyyy HH:mm")}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Shield className="w-3.5 h-3.5 text-primary shrink-0" />
                          <span className="text-sm">{log.adminName ?? log.adminId}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Scissors className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                          <span className="text-sm">{log.salonName ?? log.salonId}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={durationMs === null ? "border-amber-400 text-amber-600 bg-amber-50/30" : ""}
                        >
                          {durationStr}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {log.ipAddress ?? "—"}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ─── Componente TutorialVideosTab ─────────────────────────────────────────────
// Lista todas as seções do tutorial com campos para gerenciar URLs de vídeos.
// O Super Admin pode adicionar, editar ou remover a URL de vídeo de cada seção.

const TUTORIAL_SECTIONS = [
  // Admin (nova ordem por prioridade de uso)
  { key: "1. Configuração Inicial do Salão", profile: "Admin", sortOrder: 1 },
  { key: "2. Cadastrar Serviços", profile: "Admin", sortOrder: 2 },
  { key: "3. Cadastrar Profissionais", profile: "Admin", sortOrder: 3 },
  { key: "4. Calendário Semanal", profile: "Admin", sortOrder: 4 },
  { key: "5. Gerenciar Agendamentos", profile: "Admin", sortOrder: 5 },
  { key: "6. Gerenciar Clientes", profile: "Admin", sortOrder: 6 },
  { key: "7. Notificações", profile: "Admin", sortOrder: 7 },
  { key: "8. Gerenciar a Equipe (Acesso ao Sistema)", profile: "Admin", sortOrder: 8 },
  { key: "9. Configurar PIN de Autorização", profile: "Admin", sortOrder: 9 },
  { key: "10. Relatórios Financeiros", profile: "Admin", sortOrder: 10 },
  { key: "11. Comissões", profile: "Admin", sortOrder: 11 },
  { key: "12. Avaliações", profile: "Admin", sortOrder: 12 },
  { key: "13. Painel de Chamada (TV da Recepção)", profile: "Admin", sortOrder: 13 },
  { key: "14. Mídia Promocional no Painel de TV", profile: "Admin", sortOrder: 14 },
  { key: "15. Agente de IA: Ativar, Configurar e Testar", profile: "Admin", sortOrder: 15 },
  { key: "16. Base de Conhecimento do Agente de IA", profile: "Admin", sortOrder: 16 },
  { key: "17. Transcrição de Áudio no WhatsApp", profile: "Admin", sortOrder: 17 },
  { key: "18. Previsão do Tempo no Agente", profile: "Admin", sortOrder: 18 },
  { key: "19. Horários Especiais (Exceções)", profile: "Admin", sortOrder: 19 },
  { key: "20. Configuração SMTP para Envio de Contratos", profile: "Admin", sortOrder: 20 },
  // Funcionário
  { key: "1. Seu Dashboard Pessoal", profile: "Funcionário", sortOrder: 101 },
  { key: "2. Gerenciar Agendamentos", profile: "Funcionário", sortOrder: 102 },
  { key: "3. Agendamento Rápido de Balcão", profile: "Funcionário", sortOrder: 103 },
  { key: "4. Buscar Clientes", profile: "Funcionário", sortOrder: 104 },
  { key: "5. Enviar Confirmação por WhatsApp", profile: "Funcionário", sortOrder: 105 },
  // Caixa
  { key: "1. Múltiplas Formas de Pagamento", profile: "Caixa", sortOrder: 201 },
  { key: "2. Troco Automático (Dinheiro)", profile: "Caixa", sortOrder: 202 },
  { key: "3. Desconto com Autorização de PIN", profile: "Caixa", sortOrder: 203 },
  { key: "4. Imprimir Comanda", profile: "Caixa", sortOrder: 204 },
  { key: "5. Nota de Checkout", profile: "Caixa", sortOrder: 205 },
  { key: "6. Relatório de Caixa Completo", profile: "Caixa", sortOrder: 206 },
  { key: "7. Painel de Chamada de Clientes (TV)", profile: "Caixa", sortOrder: 207 },
  { key: "8. Estornar Pagamento (com PIN)", profile: "Caixa", sortOrder: 208 },
  { key: "9. Ver Agendamentos do Dia", profile: "Caixa", sortOrder: 209 },
  { key: "10. Atendimento Rápido de Balcão", profile: "Caixa", sortOrder: 210 },
  { key: "11. Confirmação por WhatsApp", profile: "Caixa", sortOrder: 211 },
  { key: "12. Fila de Chamada com Senha e QR Code", profile: "Caixa", sortOrder: 212 },
  { key: "13. Planos e Upgrade", profile: "Caixa", sortOrder: 213 },
];

function TutorialVideosTab() {
  const utils = trpc.useUtils();
  const { data: videoRows, isLoading } = (trpc as any).superAdmin.listTutorialVideos.useQuery();
  const upsert = (trpc as any).superAdmin.upsertTutorialVideo.useMutation({
    onSuccess: () => {
      toast.success("Vídeo salvo!");
      setEditingKey(null);
      setEditUrl("");
      utils.invalidate();
    },
    onError: (e: any) => toast.error(e.message ?? "Erro ao salvar"),
  });
  const clear = (trpc as any).superAdmin.clearTutorialVideo.useMutation({
    onSuccess: () => {
      toast.success("URL removida!");
      utils.invalidate();
    },
    onError: (e: any) => toast.error(e.message ?? "Erro ao remover"),
  });

  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editUrl, setEditUrl] = useState("");
  const [profileFilter, setProfileFilter] = useState<string>("Todos");

  // Mapa sectionKey → videoUrl atual
  const videoMap: Record<string, string | null> = {};
  if (videoRows) {
    for (const row of videoRows) {
      videoMap[row.sectionKey] = row.videoUrl ?? null;
    }
  }

  const profiles = ["Todos", "Admin", "Funcionário", "Caixa"];
  const filtered = profileFilter === "Todos"
    ? TUTORIAL_SECTIONS
    : TUTORIAL_SECTIONS.filter(s => s.profile === profileFilter);

  const withVideo = TUTORIAL_SECTIONS.filter(s => videoMap[s.key]).length;

  const handleSave = (section: typeof TUTORIAL_SECTIONS[0]) => {
    if (!editUrl.trim()) {
      toast.error("Informe a URL do vídeo");
      return;
    }
    upsert.mutate({
      sectionKey: section.key,
      sectionTitle: section.key,
      videoUrl: editUrl.trim(),
      sortOrder: section.sortOrder,
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <Card key={i} className="animate-pulse">
            <CardContent className="p-4"><div className="h-12 bg-muted rounded" /></CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div>
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-primary" />
          Gerenciar Vídeos do Tutorial
        </h2>
        <p className="text-sm text-muted-foreground mt-0.5">
          Adicione ou edite a URL de vídeo de cada seção do tutorial. Suporta YouTube, Vimeo e MP4 direto.
          Os vídeos aparecem embutidos inline para os usuários ao abrir a seção no tutorial.
        </p>
      </div>

      {/* Stats */}
      <div className="flex items-center gap-4 text-sm">
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-400 border border-blue-200 dark:border-blue-800">
          <Video className="w-3.5 h-3.5" />
          {withVideo} / {TUTORIAL_SECTIONS.length} seções com vídeo
        </div>
        <div className="text-muted-foreground">
          {TUTORIAL_SECTIONS.length - withVideo} seções ainda sem vídeo
        </div>
      </div>

      {/* Filtro por perfil */}
      <div className="flex gap-2 flex-wrap">
        {profiles.map(p => (
          <button
            key={p}
            onClick={() => setProfileFilter(p)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
              profileFilter === p
                ? "bg-primary text-primary-foreground border-primary"
                : "border-border text-muted-foreground hover:bg-muted/50"
            }`}
          >
            {p}
          </button>
        ))}
      </div>

      {/* Lista de seções */}
      <div className="space-y-2">
        {filtered.map(section => {
          const currentUrl = videoMap[section.key] ?? null;
          const isEditing = editingKey === section.key;

          return (
            <Card key={section.key} className={`transition-all ${currentUrl ? "border-blue-200 dark:border-blue-800" : ""}`}>
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  {/* Ícone de status */}
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5 ${
                    currentUrl ? "bg-blue-100 text-blue-600 dark:bg-blue-950/40" : "bg-muted text-muted-foreground"
                  }`}>
                    {currentUrl ? <Play className="w-4 h-4" /> : <Video className="w-4 h-4 opacity-40" />}
                  </div>

                  <div className="flex-1 min-w-0">
                    {/* Título e perfil */}
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm">{section.key}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        section.profile === "Admin" ? "bg-violet-100 text-violet-700 dark:bg-violet-950/30" :
                        section.profile === "Funcionário" ? "bg-blue-100 text-blue-700 dark:bg-blue-950/30" :
                        "bg-amber-100 text-amber-700 dark:bg-amber-950/30"
                      }`}>{section.profile}</span>
                    </div>

                    {/* URL atual ou campo de edição */}
                    {isEditing ? (
                      <div className="mt-2 flex gap-2">
                        <input
                          type="url"
                          value={editUrl}
                          onChange={e => setEditUrl(e.target.value)}
                          placeholder="https://www.youtube.com/watch?v=..."
                          className="flex-1 px-3 py-1.5 text-sm rounded-md border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary/30"
                          autoFocus
                          onKeyDown={e => {
                            if (e.key === "Enter") handleSave(section);
                            if (e.key === "Escape") { setEditingKey(null); setEditUrl(""); }
                          }}
                        />
                        <Button
                          size="sm"
                          onClick={() => handleSave(section)}
                          disabled={upsert.isPending}
                        >
                          {upsert.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                          Salvar
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => { setEditingKey(null); setEditUrl(""); }}
                        >
                          Cancelar
                        </Button>
                      </div>
                    ) : currentUrl ? (
                      <div className="mt-1 flex items-center gap-2">
                        <a
                          href={currentUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-blue-600 hover:underline truncate max-w-xs"
                        >
                          {currentUrl}
                        </a>
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground mt-0.5 italic">Sem vídeo configurado</p>
                    )}
                  </div>

                  {/* Ações */}
                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setEditingKey(section.key);
                        setEditUrl(currentUrl ?? "");
                      }}
                      className="h-7 px-2"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                      {currentUrl ? "Editar" : "Adicionar"}
                    </Button>
                    {currentUrl && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => clear.mutate({ sectionKey: section.key })}
                        disabled={clear.isPending}
                        className="h-7 px-2 text-destructive hover:text-destructive"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Dica de uso */}
      <Card className="border-blue-200 dark:border-blue-800 bg-blue-50/30 dark:bg-blue-950/10">
        <CardContent className="p-4 flex gap-3">
          <Play className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
          <div className="text-sm text-blue-700 dark:text-blue-400 space-y-1">
            <p className="font-semibold">Como adicionar vídeos</p>
            <p>Cole a URL do YouTube, Vimeo ou MP4 direto. O sistema detecta o tipo automaticamente e exibe o player embutido na seção correspondente do tutorial.</p>
            <p className="text-xs opacity-75">Exemplos: <code>https://youtu.be/XXXXXXXXXXX</code> · <code>https://vimeo.com/123456789</code> · <code>https://cdn.example.com/video.mp4</code></p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
